import type { MemberTutorialState, MemberTutorialUpdate } from '@mlm/shared';
import axios, { type AxiosInstance } from 'axios';
import type {
  MyTopup,
  ActivationTerms,
  RedeemPreview,
  RedeemResult,
  KitCode,
  GenealogySearchResult,
  GenealogySearchMatch,
  GenealogyResponse,
  GenealogyNode,
  PayoutBreakdownStepWire,
  PayoutRequest,
  PublicCourse,
  PayoutPreview,
  PayoutMethodOption,
  PayoutMethodField,
  CashoutWindow,
  BinaryGenealogyNodeFields,
  CaptionTrack,
  ChapterOutline,
  ContentReportReason,
  ContentReportTarget,
  CourseAccess,
  CourseAccessLevel,
  CoursePlayer,
  CourseTestState,
  CourseCatalogQuery,
  CourseSort,
  CourseSummary,
  CourseTranslation,
  CreatorAutoRenewState,
  CreatorAutoRenewStatus,
  CreatorSubscriptionPeriod,
  LedgerEntry,
  LessonFileSummary,
  LessonOutline,
  LessonProgress,
  LessonSource,
  MyCourse,
  MyCreatorSubscription,
  OrderListRow,
  OrderRow,
  RpLayout,
  Slot,
  SlotWallet,
  SlotWalletDebt,
  StorefrontConfig,
  StorefrontContext,
  StorefrontProduct,
  StorefrontResponse,
  StorefrontVariant,
  TestAttemptSummary,
  WalletTopupBracket,
  WalletTotals,
} from '@mlm/shared';
import { downloadBlob } from '@mlm/ui/lib/download';
import { useAuthStore, type AuthUser } from './auth-store';
import { queryClient } from './query-client';
import { useCartStore } from '../storefront/lib/cart-store';
import { canDownloadUrlNative, downloadUrlNative } from '@mlm/ui/lib/native-shell';

export const BASE_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:3000';

/**
 * Axios client with:
 *  - Authorization header from the in-memory token (Zustand store).
 *  - `withCredentials: true` so the refresh cookie travels on every request.
 *  - One-shot refresh-and-retry on 401. The original request is replayed
 *    once with the new token. If refresh itself fails (no cookie, expired,
 *    revoked) we clear the session and let the caller redirect to /login.
 */
export const api: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  withCredentials: true,
});

api.interceptors.request.use((config) => {
  const token = useAuthStore.getState().token;
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

/**
 * Turn a thrown request error into a human message. Axios swallows the server
 * body behind a generic "Request failed with status code 4xx"; this digs out
 * the API's `{ error, message, issues }` so toasts/dialogs show the real reason.
 */
export function apiErrorMessage(e: unknown, fallback = 'Request failed'): string {
  if (axios.isAxiosError(e)) {
    const data = e.response?.data as
      | {
          error?: string;
          message?: string;
          issues?: Array<{ path?: (string | number)[]; message?: string }>;
        }
      | undefined;
    if (data?.issues?.length) {
      return data.issues
        .map((i) => `${(i.path ?? []).join('.') || 'value'}: ${i.message ?? 'invalid'}`)
        .join('; ');
    }
    if (data?.message) return data.message;
    if (data?.error) return data.error;
    if (e.message) return e.message;
  }
  if (e instanceof Error && e.message) return e.message;
  return fallback;
}

let refreshInFlight: Promise<string | null> | null = null;

async function refreshTokenOnce(): Promise<string | null> {
  if (!refreshInFlight) {
    refreshInFlight = (async () => {
      try {
        const res = await axios.post<{ accessToken: string }>(
          `${BASE_URL}/auth/refresh`,
          {},
          { withCredentials: true },
        );
        return res.data.accessToken;
      } catch {
        return null;
      } finally {
        // Reset after a tick so concurrent 401s share this attempt.
        setTimeout(() => {
          refreshInFlight = null;
        }, 0);
      }
    })();
  }
  return refreshInFlight;
}

api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original = error.config;
    if (
      error.response?.status === 401 &&
      original &&
      !original._retried &&
      !String(original.url ?? '').includes('/auth/refresh') &&
      !String(original.url ?? '').includes('/auth/login') &&
      // `/me/password` answers a WRONG CURRENT PASSWORD with 401. That's a
      // business error, not an expired token, so refreshing and replaying it
      // just burns a second attempt against the route's per-hour limit —
      // members hit the "too many attempts" wall in half the tries they made.
      !String(original.url ?? '').includes('/me/password')
    ) {
      original._retried = true;
      const newToken = await refreshTokenOnce();
      if (newToken) {
        const user = useAuthStore.getState().user;
        if (user) useAuthStore.getState().setSession(newToken, user);
        original.headers.Authorization = `Bearer ${newToken}`;
        return api.request(original);
      }
      useAuthStore.getState().clear();
      queryClient.clear();
    }
    return Promise.reject(error);
  },
);

export async function login(args: {
  // Email OR username (when the tenant allows username login). Sent as
  // `identifier`; the server resolves email-or-username for member origins.
  identifier: string;
  password: string;
  totp?: string;
}): Promise<{ accessToken: string; user: AuthUser }> {
  const res = await api.post<{ accessToken: string; user: AuthUser }>('/auth/login', args);
  return res.data;
}

/**
 * QoL Slice 4 — exchange a single-use grant token for an impersonation
 * session. Called by the /impersonate route when the admin SPA opened this
 * tab. Sets the impersonation refresh cookie on THIS (member) origin.
 */
export async function exchangeImpersonationToken(
  token: string,
): Promise<{ accessToken: string; user: AuthUser }> {
  const res = await api.post<{ accessToken: string; user: AuthUser }>(
    '/auth/impersonation-exchange',
    { token },
  );
  return res.data;
}

/**
 * QoL Slice 4 — end the current impersonation session. Revokes the
 * impersonation refresh family + clears the cookie; the admin's own session
 * (on the admin origin) is untouched.
 */
export async function endImpersonation(): Promise<void> {
  try {
    await api.post('/auth/end-impersonation');
  } catch {
    /* best-effort — we clear the in-memory session regardless */
  }
}

/** sessionStorage key suppressing the soft unpaid-orders dialog for the rest of
 *  the tab session. Defined HERE (not in lock-inputs, which re-exports it)
 *  because `logout()` below must clear it and lock-inputs already imports from
 *  this module — the other direction would be a cycle. */
export const UNPAID_DIALOG_SESSION_FLAG = 'omni.unpaidDialogShown';

export async function logout(): Promise<void> {
  try {
    await api.post('/auth/logout');
  } catch {
    /* ignore — clear local state regardless */
  }
  useAuthStore.getState().clear();
  // Revert any member-discounted cart lines to base price now that we're signed out.
  useCartStore.getState().revertToBasePrice();
  // Re-arm the unpaid-orders dialog for the next login in this tab. Its
  // docstring always promised this; nothing actually did it until the login
  // resume-redirect made the stale flag user-visible.
  if (typeof window !== 'undefined') window.sessionStorage.removeItem(UNPAID_DIALOG_SESSION_FLAG);
  // Drop every cached query so the next signed-in user can't see the
  // previous account's data flash before refetches complete.
  queryClient.clear();
}

export async function signup(args: {
  email: string;
  password: string;
  confirmPassword: string;
  firstName: string;
  middleName?: string;
  lastName: string;
  phone?: string;
  username?: string;
  sponsorCode?: string;
  // Per-package link: the kit product id the prospect arrived through (`?pkg=`).
  pkg?: string;
  // Replicated campaign slug the lead arrived through (`/r/<ref>/<slug>` → `?c=`).
  campaignSlug?: string;
  termsAccepted?: boolean;
  termsVersion?: number;
  registrationData?: Record<string, string | { r2Key: string; filename: string }>;
  /** `YYYY-MM-DD`, when the tenant asks for it. */
  birthDate?: string;
}): Promise<{ id: string; email: string; referralCode: string | null }> {
  const res = await api.post('/auth/signup', args);
  return res.data;
}

export type SponsorPreview = {
  slotCode: string;
  ownerFullName: string | null;
  ownerEmail: string;
  membershipCode: string;
  slotType: 'paid' | 'free' | 'cd' | 'half_cd';
};

/** Resolve a referral/slot code to its sponsor details so signup can confirm
 *  the sponsor before creating the account. Throws 404 sponsor_not_found. */
export async function previewSignupSponsor(sponsorCode: string): Promise<SponsorPreview> {
  const res = await api.post<SponsorPreview>('/auth/signup/preview-sponsor', { sponsorCode });
  return res.data;
}

/** Live username-availability check for the signup form. */
export async function checkUsernameAvailable(
  u: string,
): Promise<{ required: boolean; valid: boolean; available: boolean }> {
  const res = await api.get('/auth/username-available', { params: { u } });
  return res.data;
}

// ── Terms & onboarding (BF1) ─────────────────────────────────────────────
export type TermsInfo = {
  termsText: string;
  termsVersion: number;
  privacyText: string;
  privacyVersion: number;
  requireOnSignup: boolean;
  requireUsernameOnSignup?: boolean;
};

/** Public — Terms + Privacy text shown on the signup page (no auth). */
export async function getTerms(): Promise<TermsInfo> {
  const res = await api.get<TermsInfo>('/auth/terms');
  return res.data;
}

/** Record acceptance of the CURRENT terms version (re-acceptance gate). */
export async function acceptTerms(): Promise<{ ok: boolean; termsVersionAccepted: number }> {
  const res = await api.post('/me/accept-terms');
  return res.data;
}

export type OnboardingStage =
  | 'registered'
  | 'payment_pending'
  | 'code_in_vault'
  | 'activated'
  | 'rolling';

export type OnboardingState = {
  stage: OnboardingStage;
  signals: {
    activeSlotCount: number;
    unredeemedCodeCount: number;
    pendingKitOrder: boolean;
    leadsCount: number;
    /** Live codes that can create a slot (an upgrade-only code cannot). */
    activationCodeCount: number;
    /** The newest kit order still in progress, if any. */
    pendingKitOrderId: string | null;
    /** That order is waiting on the member's payment, with no proof in yet. */
    pendingKitOrderNeedsPayment: boolean;
  };
  tutorial: {
    /** Null = not in line for the automatic tutorials; the button still works. */
    state: MemberTutorialState | null;
    /** A kit order can create the slot by itself, so the Vault step may not happen. */
    slotMayAutoCreate: boolean;
  };
};

export async function getOnboarding(): Promise<OnboardingState> {
  const res = await api.get<OnboardingState>('/me/onboarding');
  return res.data;
}

export async function updateMemberTutorial(
  body: MemberTutorialUpdate,
): Promise<{ state: MemberTutorialState }> {
  const res = await api.post<{ state: MemberTutorialState }>('/me/tutorial', body);
  return res.data;
}

export async function listMyCodes(opts?: { onlyAvailable?: boolean }): Promise<KitCode[]> {
  const res = await api.get<KitCode[]>('/me/codes', {
    params: opts?.onlyAvailable ? { available: '1' } : {},
  });
  return res.data;
}

/** F6: a PV-carrying product code in the member's Vault. */
export type ProductCode = {
  id: string;
  code: string;
  pin: string;
  pv: string;
  redeemedAt: string | null;
  createdAt: string;
  /** When the source order was placed; null if the order row is gone. */
  purchasedAt: string | null;
  /** Human-readable order/transaction number that generated this code. */
  orderCode: string | null;
  /** First line item's product name on the source order (null if unavailable). */
  primaryProductName: string | null;
  /** Number of line items on the source order (for a "+N more" suffix). */
  itemCount: number;
};

export async function listMyProductCodes(): Promise<ProductCode[]> {
  const res = await api.get<ProductCode[]>('/me/product-codes');
  return res.data;
}

export async function redeemProductCode(
  code: string,
  pin: string,
): Promise<{ redeemed: boolean; pv: string }> {
  const res = await api.post<{ redeemed: boolean; pv: string }>('/product-codes/redeem', {
    code,
    pin,
  });
  return res.data;
}

export async function redeemKit(args: {
  code: string;
  pin: string;
  sponsorCode?: string;
  anchorSlotId?: string;
  side?: 'L' | 'R';
  termsAccepted?: boolean;
  termsVersion?: number;
}): Promise<RedeemResult> {
  const res = await api.post<RedeemResult>('/kits/redeem', args);
  return res.data;
}

/** Non-committing dry run: resolves the sponsor so the member can confirm. */
export async function previewRedeem(args: {
  code: string;
  pin: string;
  sponsorCode?: string;
}): Promise<RedeemPreview> {
  const res = await api.post<RedeemPreview>('/kits/redeem/preview', args);
  return res.data;
}

export async function getActivationTerms(): Promise<ActivationTerms> {
  const res = await api.get<ActivationTerms>('/kits/activation-terms');
  return res.data;
}

// F4: activate a warm lead's slot with a Code+PIN from the caller's own vault.
// The new slot is owned by the lead, sponsored by the caller.
export async function activateLeadWithCode(args: {
  userId: string;
  code: string;
  pin: string;
  anchorSlotId?: string;
  side?: 'L' | 'R';
  termsAccepted?: boolean;
  termsVersion?: number;
}): Promise<RedeemResult> {
  const { userId, ...body } = args;
  const res = await api.post<RedeemResult>(`/me/leads/${userId}/activate`, body);
  return res.data;
}

// F4 (extended): spend a Code+PIN from the caller's vault to create a slot for
// ANY downline member, matched by email (+ username double-confirm on tenants
// that use usernames). Allows extra slots for already-active members; the slot
// limit is enforced server-side.
export async function redeemForDownline(args: {
  recipientUsername?: string;
  recipientEmail: string;
  code: string;
  pin: string;
  anchorSlotId?: string;
  side?: 'L' | 'R';
  termsAccepted?: boolean;
  termsVersion?: number;
}): Promise<RedeemResult> {
  const res = await api.post<RedeemResult>('/me/codes/redeem-for-downline', args);
  return res.data;
}

// Feature E: transfer an unredeemed vault code to a downline member, matched by
// email (+ username double-confirm on tenants that use usernames). The code
// leaves the caller's vault on success.
export async function transferCodeToDownline(args: {
  codeId: string;
  recipientUsername?: string;
  recipientEmail: string;
}): Promise<{ ok: boolean; recipientUserId: string }> {
  const { codeId, ...body } = args;
  const res = await api.post<{ ok: boolean; recipientUserId: string }>(
    `/me/codes/${codeId}/transfer`,
    body,
  );
  return res.data;
}

// BF2: upgrade an existing slot with a Code+PIN (free->free/paid, paid->paid).
export type UpgradeSlotResult = {
  slotId: string;
  afterMembershipCode: string;
  beforeSlotType: 'paid' | 'free';
  afterSlotType: 'paid' | 'free';
  becamePaid: boolean;
};

export async function upgradeSlot(
  slotId: string,
  args: { code: string; pin: string },
): Promise<UpgradeSlotResult> {
  const res = await api.post<UpgradeSlotResult>(`/slots/${slotId}/upgrade`, args);
  return res.data;
}

export async function listMySlots(): Promise<Slot[]> {
  const res = await api.get<Slot[]>('/slots');
  return res.data;
}

/**
 * Phase 13: hydrate session from the refresh cookie on page reload. Mirrors
 * admin's `restoreSession()`. Called once on App boot.
 */
export async function restoreSession(): Promise<boolean> {
  try {
    const refreshRes = await axios.post<{ accessToken: string }>(
      `${BASE_URL}/auth/refresh`,
      {},
      { withCredentials: true },
    );
    const token = refreshRes.data.accessToken;
    const meRes = await axios.get<AuthUser>(`${BASE_URL}/me`, {
      withCredentials: true,
      headers: { Authorization: `Bearer ${token}` },
    });
    useAuthStore.getState().setSession(token, meRes.data);
    return true;
  } catch {
    useAuthStore.getState().clear();
    queryClient.clear();
    return false;
  }
}

// ── Wallet ─────────────────────────────────────────────────────────────

export async function getSlotWallet(slotId: string | null): Promise<SlotWallet> {
  const res = await api.get<SlotWallet>('/wallet', {
    params: slotId ? { slotId } : {},
  });
  return res.data;
}

export async function getWalletTotals(): Promise<WalletTotals> {
  const res = await api.get<WalletTotals>('/wallet/totals');
  return res.data;
}

/** Lifetime "Total saved" figures (member-level, all slots). `enabled:false`
 *  when the tenant's `total_saved_counter_enabled` flag is off. */
export type TotalSaved = {
  enabled: boolean;
  totals: { total: string; ecom: string; pos: string } | null;
};

export async function getTotalSaved(): Promise<TotalSaved> {
  const res = await api.get<TotalSaved>('/me/total-saved');
  return res.data;
}

export async function getLedger(args: {
  slotId?: string;
  /** Filter to a single comp plan (source_plan), e.g. for an earnings-type drilldown. */
  plan?: string;
  page?: number;
  pageSize?: number;
}): Promise<LedgerEntry[]> {
  const res = await api.get<LedgerEntry[]>('/wallet/ledger', {
    params: {
      slotId: args.slotId,
      plan: args.plan,
      page: args.page ?? 1,
      pageSize: args.pageSize ?? 50,
    },
  });
  return res.data;
}

// ── Storefront ─────────────────────────────────────────────────────────
/** A purchasable product variation surfaced on the storefront. */

// The storefront wire shapes live in @mlm/shared: the coach app sells the same
// catalogue and may not import from this app (CLAUDE.md). Re-exported so every
// existing `from '../lib/api'` import in this SPA keeps working unchanged.
export type { LedgerEntry, Slot, SlotWallet, SlotWalletDebt, WalletTotals };
export type { GenealogyNode, GenealogyResponse, GenealogySearchMatch };
export type { ActivationTerms, KitCode, MyTopup, RedeemPreview, RedeemResult };
/** The shared name for this shape is GenealogySearchResult; aliased so this SPA's call sites are unchanged. */
export type GenealogySearchResponse = GenealogySearchResult;
export type { CashoutWindow, PayoutMethodField, PayoutMethodOption, PayoutPreview, PayoutRequest };

// The LMS wire shapes moved to @mlm/shared for the same reason as the
// storefront ones below: the coach app plays the same courses.
export type {
  CaptionTrack,
  ChapterOutline,
  CourseAccess,
  CourseAccessLevel,
  CoursePlayer,
  CourseTestState,
  LessonOutline,
  LessonFileSummary,
  LessonProgress,
  LessonSource,
  MyCourse,
  TestAttemptSummary,
};
// The catalog, course page and creator-subscription shapes followed: the coach
// app's Learn tab browses and sells the same courses.
export type {
  CourseCatalogQuery,
  CourseSort,
  CourseSummary,
  CourseTranslation,
  CreatorAutoRenewState,
  CreatorAutoRenewStatus,
  CreatorSubscriptionPeriod,
  MyCreatorSubscription,
  PublicCourse,
};

// The storefront wire shapes now live in @mlm/shared (the coach app sells the same
// catalogue and may not import from this app). Re-exported so every existing
// `from '../lib/api'` import in this SPA keeps working unchanged.
export type {
  OrderListRow,
  OrderRow,
  StorefrontContext,
  StorefrontProduct,
  StorefrontResponse,
  StorefrontVariant,
};

/** Public catalog image URL — the API 302-redirects to a presigned object URL. */
export function productImageUrl(productId: string, idx: number): string {
  return `${BASE_URL}/products/${productId}/image/${idx}`;
}

export function productReviewPhotoUrl(productId: string, reviewId: string, index = 0): string {
  const q = index > 0 ? `?i=${index}` : '';
  return `${BASE_URL}/products/${productId}/reviews/${reviewId}/photo${q}`;
}

/** Public category tile image — the API 302-redirects to a presigned object URL. */
export function categoryImageUrl(id: string): string {
  return `${BASE_URL}/categories/${id}/image`;
}

// ── Announcements (admin-authored, shown on the Dashboard) ──────────────────
export type Announcement = {
  id: string;
  title: string;
  body: string;
  publishedAt: string | null;
  imageUrl: string | null;
};

export async function listAnnouncements(): Promise<Announcement[]> {
  const res = await api.get<Announcement[]>('/me/announcements');
  return res.data;
}

/** Banner image for an announcement — the API 302-redirects to a presigned URL. */
export function announcementImageUrl(id: string): string {
  return `${BASE_URL}/me/announcements/${id}/image`;
}

export type ProductReviewItem = {
  id: string;
  rating: number;
  comment: string | null;
  hasPhoto: boolean;
  photoCount: number;
  aspectTags: string[];
  createdAt: string;
  reviewerName: string;
};
export type ProductReviewsResponse = {
  summary: {
    count: number;
    average: number;
    distribution: Record<string, number>;
    withPhotos: number;
    withComments: number;
  };
  reviews: ProductReviewItem[];
};
export type ProductReviewFilter = { rating?: number; hasPhoto?: boolean; hasComment?: boolean };
export async function listProductReviews(
  productId: string,
  filter?: ProductReviewFilter,
): Promise<ProductReviewsResponse> {
  const res = await api.get<ProductReviewsResponse>(`/products/${productId}/reviews`, {
    params: {
      ...(filter?.rating ? { rating: filter.rating } : {}),
      ...(filter?.hasPhoto ? { hasPhoto: true } : {}),
      ...(filter?.hasComment ? { hasComment: true } : {}),
    },
  });
  return res.data;
}

export type ProductTestimonial = {
  id: string;
  title: string;
  youtubeUrl: string | null;
  bodyHtml: string | null;
  authorName: string | null;
};
export async function listProductTestimonials(productId: string): Promise<ProductTestimonial[]> {
  const res = await api.get<ProductTestimonial[]>(`/products/${productId}/testimonials`);
  return res.data;
}

export type Category = {
  id: string;
  code: string;
  name: string;
  parentId: string | null;
  imageKey?: string | null;
};

export async function listCategories(): Promise<Category[]> {
  const res = await api.get<Category[]>('/categories');
  return res.data;
}

export async function getStorefrontProduct(
  id: string,
  slotId?: string | null,
): Promise<{ context: StorefrontResponse['context']; product: StorefrontProduct }> {
  const res = await api.get<{ context: StorefrontResponse['context']; product: StorefrontProduct }>(
    `/storefront/products/${id}`,
    { params: slotId ? { slotId } : {} },
  );
  return res.data;
}

/**
 * `slotId` is the member's SELECTED slot. Without it the server prices the
 * catalog against their EARLIEST active slot, which is a different tier for any
 * member holding slots at more than one membership — they see one price and get
 * charged another. Always pass it when the caller has it.
 */
export async function getStorefront(
  search?: string,
  seller?: string,
  slotId?: string | null,
): Promise<StorefrontResponse> {
  const res = await api.get<StorefrontResponse>('/storefront/products', {
    params: {
      ...(search?.trim() ? { search: search.trim() } : {}),
      ...(seller ? { seller } : {}),
      ...(slotId ? { slotId } : {}),
    },
  });
  return res.data;
}

// ── Public storefront (no auth — landing + public shop + checkout) ───────
/** Branding-safe tenant config from the public `GET /tenant/config`. */
export type TenantConfig = {
  name: string;
  logoUrl: string | null;
  coinLabel: string;
  baseCurrency: string;
  /** Admin-editable congratulatory message for the storefront order-placed dialog. */
  orderSuccessMessage?: string;
  /** Admin-editable message shown on login when the member has unpaid orders. */
  unpaidOrdersDialogMessage?: string;
  /** Max slots a member may own (null = unlimited). Shown as current/limit. */
  maxSlotsPerMember?: number | null;
  /** Gates the redeem form's "Place in my binary tree now" checkbox (default off). */
  manualPlacementOnRedeem?: boolean;
  /** Gates the genealogy tree's "Activate New Slot" action card (default on). */
  genealogyActivateSlot?: boolean;
  /** When on, new slots auto-place via spillover, so the binary tree's manual
   * "Activate New Slot" (place-a-seat) affordance is hidden (default off). */
  autoPlaceSlots?: boolean;
  /** F6: purchases mint PV-carrying product codes into the Vault (default off). */
  productCodeGeneration?: boolean;
  /** Show the member's sponsor on the dashboard "Your standing" card (default off). */
  showSponsorOnDashboard?: boolean;
  /** Which detail fields show on each box of the binary genealogy tree. */
  binaryGenealogyNodeFields?: BinaryGenealogyNodeFields;
  /** When false, hide the sponsor field on redeem for overridden members (default on). */
  showSponsorOnRedeem?: boolean;
  /** Dropshipping Income master switch — gates the product Share + dropship checkout. */
  dropshippingIncome?: boolean;
  /** Store-wide Share & Earn link (/s/{ref}) — share surface only; money stays on dropshippingIncome. */
  storeWideDropshipLink?: boolean;
  /** Master show/hide for replicated-link packages (Invite "Package links" card + kit scoping; default on). */
  packageReplicatedLinkEnabled?: boolean;
  /** Storefront home: drop the curated Featured Packages from the product grid below them (default off). */
  productFeedHideFeaturedPackages?: boolean;
  /** Storefront home: drop `products.featured` products from the product grid (default off). */
  productFeedHideFeaturedProducts?: boolean;
  /** Member dashboard "Total saved" card — lifetime discount sum (default off). */
  totalSavedCounter?: boolean;
  /** Order-by-Area: server auto-routes the fulfilling branch by zone — hide the branch picker. */
  orderByArea?: boolean;
  /** BF4: wallet (GC/coin) orders with an address charge shipping + walk the ladder. */
  walletOrdersChargeShipping?: boolean;
  /** All-virtual carts skip the storefront checkout and go straight to the
   *  locked payment page. Default off. */
  virtualInstantPay?: boolean;
  /** Campaign-page signups with an unpaid order are held on the payment page.
   *  Default off. */
  unpaidOrderLockEnabled?: boolean;
  /** A fresh login resumes an interrupted virtual checkout (unpaid virtual
   *  order → its pay page, else all-virtual cart → /checkout). One-shot. */
  virtualCheckoutResumeOnLogin?: boolean;
  /** Feature E: gates the Code Vault "Transfer to downline" action. */
  kitCodesDownlineTransfer?: boolean;
  /** Chat (M-chat): gates the "Message seller" button + chat bell. */
  messagingEnabled?: boolean;
  /** Auto-Ship: gates the member Auto-Ship nav item + routes. */
  autoshipEnabled?: boolean;
  /** Member subscription: gates the Subscribe + Live Links nav items. */
  memberSubscriptionEnabled?: boolean;
  /** AI Assistant: gates the member Assistant nav item + /assistant page (default off). */
  assistantEnabled?: boolean;
  /** Land on /assistant after login (already conjoined with assistantEnabled). */
  assistantHomePromotion?: boolean;
  /** Resources library: gates the member Resources nav item + /resources (default off). */
  memberResourcesEnabled?: boolean;
  /** LMS master switch — when false the /my-courses, /learn, /course routes and nav are absent. */
  lmsEnabled?: boolean;
  /** Phase 8: member-facing Report affordances. Already ANDed with `lmsEnabled`
   *  server-side, so the SPA reads it on its own. */
  abuseReportingEnabled?: boolean;
  /** LMS: 'mlm' (passbook, default) | 'learning' (classroom dashboard + relabeled nav). Always 'mlm' when lmsEnabled is false. */
  memberDashboardMode?: 'mlm' | 'learning';
  /** LMS learning mode: show the Invite-a-friend card on the classroom dashboard. */
  showTeamEarnings?: boolean;
  /** What archiving does here: reversible deactivation, or an erasure. */
  accountArchiveMode?: 'deactivate' | 'erase';
  /** When true, a lesson cannot be scrubbed past what has been watched. */
  lmsSeekRestriction?: boolean;
  /** When false, learners get no "Mark complete" button (see `manualCompleteVisible`). */
  lmsManualCompletion?: boolean;
  /**
   * Phase 10: who sells access to a `subscriber` course. 'tenant' = the legacy
   * one-plan-unlocks-everything wallet subscription; 'creator' = per-creator
   * monthly / 6-month / 12-month plans. Already ANDed with `lmsEnabled`.
   */
  lmsSubscriptionModel?: 'tenant' | 'creator';
  /** Must a member hold an activated slot to buy a creator subscription? */
  lmsSubscriptionRequiresSlot?: boolean;
  /**
   * The tenant forces every LINK SIGNUP under one configured slot, so a slot
   * picker can't change who a signup lands under (purchase attribution still
   * follows the link). Boolean only — the slot code is never exposed.
   */
  linkSponsorOverrideActive?: boolean;
  /** Render Office docs inline via Microsoft's viewer (default off). */
  memberResourcesOfficeViewer?: boolean;
  /** Registration gating: 'on' | 'links_only' | 'by_sponsor_only'. */
  signupMode?: 'on' | 'links_only' | 'by_sponsor_only';
  /**
   * Information-site-only mode: no shop, no membership, no users — just this
   * tenant's own landing pages. `App` mounts a reduced route tree and the
   * public chrome drops cart / search / login / sign-up / account. Read it
   * through `useCommerceEnabled()`, never directly, so the default stays
   * "commerce on" when the config hasn't loaded.
   */
  publicSiteInfoOnly?: boolean;
  /** An enabled campaign page (`?c=<slug>`) satisfies the restrictive signup
   *  modes; that visitor registers under the walk-in default sponsor. */
  campaignPageSignupAllowed?: boolean;
  /** A visitor who is not signed in may browse the courses (the API enforces it too). */
  lmsPublicCatalog?: boolean;
  /** Virtual products open their assigned replicated page instead of /product/:id. */
  virtualProductsOpenReplicatedPage?: boolean;
  /** A funnel sale (`/buy/:productId`) is attributed to the inviter as a dropship
   *  sale. Requires `dropshippingIncome` — see the flag's doc in tenant-features. */
  virtualFunnelDropshipAttribution?: boolean;
  /** Ranks showcase (storefront section + dashboard panel) is on (default off). */
  rankShowcase?: boolean;
  /** Gift checks (cashier phase 4): gates the member page + nav item (default off). */
  vouchersEnabled?: boolean;
  /** What the tenant calls a gift check, singular ("Gift Check"). */
  voucherLabel?: string;
  /** Guided tutorials + the Tutorial button (default off). */
  memberTutorialEnabled?: boolean;
  /** Whether the signup form and the profile ask for a birth date (cashier phase 5). */
  birthDateField?: 'hidden' | 'optional' | 'required';
  /** Admin-defined extra signup fields (text/file), sorted by display order. */
  registrationFields?: import('@mlm/shared').RegistrationField[];
  /** Per-tenant theme tokens, injected as CSS vars at boot. See `useTenantTheme`. */
  theme?: import('@mlm/shared').ThemeTokens | null;
};

export type RegistrationFileValue = { r2Key: string; filename: string };
/** Upload a registration `file` field; returns the stored object reference. */
export async function uploadRegistrationFile(file: File): Promise<RegistrationFileValue> {
  const form = new FormData();
  form.append('file', file);
  const res = await api.post<RegistrationFileValue>('/auth/registration-upload', form);
  return res.data;
}

export type KycDocumentRef = { kind: string; r2Key: string };
/** Upload one KYC document image; returns the stored object reference. */
export async function uploadKycDocument(file: File): Promise<RegistrationFileValue> {
  const form = new FormData();
  form.append('file', file);
  const res = await api.post<RegistrationFileValue>('/me/kyc/upload', form);
  return res.data;
}

/** Upload/replace the member's profile picture; returns a presigned read URL. */
export async function uploadProfilePicture(file: File): Promise<{ profilePictureUrl: string }> {
  const form = new FormData();
  form.append('file', file);
  const res = await api.post<{ profilePictureUrl: string }>('/me/profile-picture', form);
  return res.data;
}

/** Remove the member's profile picture (revert to initials). */
export async function removeProfilePicture(): Promise<{ ok: boolean }> {
  const res = await api.delete<{ ok: boolean }>('/me/profile-picture');
  return res.data;
}

/** Submit collected KYC document references for admin review. */
export async function submitKyc(documents: KycDocumentRef[]): Promise<{ id: string }> {
  const res = await api.post<{ id: string }>('/me/kyc', { documents });
  return res.data;
}

export type KycRequirement = {
  key: string;
  label: string;
  description?: string;
  required: boolean;
};
/** Admin-defined KYC document slots the member must upload. */
export async function getKycRequirements(): Promise<KycRequirement[]> {
  const res = await api.get<KycRequirement[]>('/me/kyc/requirements');
  return res.data;
}

export async function getTenantConfig(): Promise<TenantConfig> {
  const res = await api.get<TenantConfig>('/tenant/config');
  return res.data;
}

/**
 * Raw product row from the PUBLIC `GET /products`. No per-member pricing — only
 * base `price` (cash). Mirrors the catalog row the admin app consumes.
 */
export type PublicProduct = {
  id: string;
  sku: string;
  name: string;
  description: string | null;
  /** Sanitized rich product copy (Details tab). null = none. */
  detailsHtml: string | null;
  videoUrl: string | null;
  price: string;
  pv: string;
  priceGc: string | null;
  priceCoin: string | null;
  images: string[];
  categoryId: string | null;
  /** Physical dimensions for the derived Specifications table. "0.0000" = unset. */
  weightKg: string;
  lengthCm: string;
  widthCm: string;
  heightCm: string;
  active: boolean;
  featured: boolean;
  isKit: boolean;
  /** Virtual product / service: nothing is delivered. `GET /products` has always
   *  shipped this column — the type simply never declared it, so the logged-out
   *  storefront could not tell a virtual product from a physical one. */
  isVirtual: boolean;
  /** Slug of the ENABLED campaign page this product sells through.
   *
   *  OPTIONAL because it genuinely isn't always there: the LIST route
   *  (`GET /products`) joins `replicated_pages` and sends it, but the detail
   *  route (`GET /products/:id`) goes through `findProductById`, a bare
   *  `SELECT * FROM products` with no join. Declaring it required made the
   *  compiler vouch for a field the detail payload never carries. */
  replicatedPageSlug?: string | null;
  /** M1 marketplace: set = merchant-listed product, null/absent = house product. */
  merchantUserId?: string | null;
};

/** Active catalog only — never leak inactive products to the public. */
export async function listPublicProducts(
  search?: string,
  seller?: string,
): Promise<PublicProduct[]> {
  const res = await api.get<PublicProduct[]>('/products', {
    params: {
      activeOnly: true,
      pageSize: 200,
      ...(search?.trim() ? { search: search.trim() } : {}),
      ...(seller ? { seller } : {}),
    },
  });
  return res.data;
}

export async function getPublicProduct(id: string): Promise<PublicProduct> {
  const res = await api.get<PublicProduct>(`/products/${id}`);
  return res.data;
}

/** Public storefront flash-sale strip (admin-configured product ids + countdown). */
export type FlashSaleResponse = {
  enabled: boolean;
  endsAt: string | null;
  products: PublicProduct[];
};
export async function getFlashSale(): Promise<FlashSaleResponse> {
  const res = await api.get<FlashSaleResponse>('/storefront/flash-sale');
  return res.data;
}

/** Public landing "packages": admin-curated, ORDERED product rows for a tenant landing's packages section. */
export async function getLandingPackages(): Promise<{ products: PublicProduct[] }> {
  const res = await api.get<{ products: PublicProduct[] }>('/storefront/landing-packages');
  return res.data;
}

export type FeaturedPackagesResponse = {
  enabled: boolean;
  heading: string | null;
  products: PublicProduct[];
};
/** Public storefront "Featured Packages": admin-curated, ORDERED product rows for the featuredPackages section. */
export async function getFeaturedPackages(): Promise<FeaturedPackagesResponse> {
  const res = await api.get<FeaturedPackagesResponse>('/storefront/featured-packages');
  return res.data;
}

/** Public storefront CMS content (hero, banners, vouchers, footer, etc.). */
export async function getStorefrontContent(): Promise<StorefrontConfig> {
  const res = await api.get<StorefrontConfig>('/storefront/content');
  return res.data;
}

/** Public storefront "Contact us" form submission. */
export async function submitContactInquiry(args: {
  name: string;
  email: string;
  subject: string;
  message: string;
}): Promise<{ ok: boolean }> {
  const res = await api.post<{ ok: boolean }>('/storefront/contact', args);
  return res.data;
}

/** Public storefront asset URL — the API 302-redirects to a presigned object URL. */
export function storefrontAssetUrl(key: string): string {
  return `${BASE_URL}/storefront/asset?key=${encodeURIComponent(key)}`;
}

/** Public tenant brand-logo URL — the API 302-redirects to a presigned object URL. */
export function tenantLogoUrl(which: 'light' | 'dark' | 'favicon'): string {
  return `${BASE_URL}/tenant/logo/${which}`;
}

/** Resolve an API-relative asset path (e.g. a rank's `/ranks/<code>/icon`) to a full URL. */
export function apiPathUrl(path: string): string {
  return `${BASE_URL}${path}`;
}

// ── Ranks showcase (public; 404 while `rank_showcase_enabled` is off) ────────
export type ShowcaseRank = {
  code: string;
  name: string | null;
  description: string | null;
  /** API-relative icon path, or null when the rank has no icon. */
  iconUrl: string | null;
  index: number;
  achieverCount: number;
};

export type RankAchiever = { name: string; avatarUrl: string | null; achievedAt: string };

/** Ranks in config order. The feature-off 404 reads as "no ranks". */
export async function getRanks(): Promise<ShowcaseRank[]> {
  try {
    const res = await api.get<{ ranks: ShowcaseRank[] }>('/ranks');
    return res.data.ranks;
  } catch (e) {
    if (axios.isAxiosError(e) && e.response?.status === 404) return [];
    throw e;
  }
}

/** One page of a rank's achievers (masked names), newest first. */
export async function getRankAchievers(args: {
  code: string;
  cursor?: string;
  limit?: number;
}): Promise<{ items: RankAchiever[]; nextCursor: string | null }> {
  const res = await api.get<{ items: RankAchiever[]; nextCursor: string | null }>(
    `/ranks/${encodeURIComponent(args.code)}/achievers`,
    { params: { cursor: args.cursor, limit: args.limit ?? 24 } },
  );
  return res.data;
}

/** Per-category product counts over the public-catalog visibility predicate.
 * `seller='house'` restricts to the in-house official-store catalog. */
export type CategoryCounts = { byCategory: Record<string, number>; total: number };
export async function getCategoryCounts(seller?: 'house'): Promise<CategoryCounts> {
  const res = await api.get<CategoryCounts>('/storefront/category-counts', {
    params: seller ? { seller } : {},
  });
  return res.data;
}

/** Popular search tags auto-derived from real sales (falls back to the curated
 * CMS suggestions server-side when there isn't enough sales data). */
export async function getPopularTags(): Promise<string[]> {
  const res = await api.get<string[]>('/storefront/popular-tags');
  return res.data;
}

/** Public merchant storefront profile + real stats (Chat Response omitted). */
export type MerchantStats = {
  merchantUserId: string;
  businessName: string;
  joinedAt: string;
  productCount: number;
  rating: { average: number; count: number };
  followers: number;
  following: number;
  transactions: number;
  categories: { id: string; name: string }[];
};
export async function getMerchantStats(id: string): Promise<MerchantStats> {
  const res = await api.get<MerchantStats>(`/merchants/${id}`);
  return res.data;
}

export type ShopSearchResult = {
  merchantUserId: string;
  businessName: string;
  productCount: number;
  followers: number;
  rating: { average: number; count: number };
};
/** Shops whose name matches a search term ("Shops related to …" band). */
export async function searchShops(search: string, limit?: number): Promise<ShopSearchResult[]> {
  const res = await api.get<{ shops: ShopSearchResult[] }>('/storefront/shops', {
    params: { search, limit },
  });
  return res.data.shops;
}

export type MerchantTransaction = {
  code: string;
  status: string;
  total: string;
  itemCount: number;
  createdAt: string;
};
export async function listMerchantTransactions(
  id: string,
  opts?: { limit?: number; offset?: number },
): Promise<MerchantTransaction[]> {
  const res = await api.get<{ transactions: MerchantTransaction[] }>(
    `/merchants/${id}/transactions`,
    { params: opts },
  );
  return res.data.transactions;
}

export type MerchantReview = {
  id: string;
  rating: number;
  comment: string | null;
  hasPhoto: boolean;
  photoCount: number;
  productId: string;
  productName: string;
  reviewerName: string;
  featured: boolean;
  createdAt: string;
};
export async function listMerchantReviews(
  id: string,
  opts?: { limit?: number; offset?: number; rating?: number },
): Promise<MerchantReview[]> {
  const res = await api.get<{ reviews: MerchantReview[] }>(`/merchants/${id}/reviews`, {
    params: opts,
  });
  return res.data.reviews;
}

/** Reviews the merchant pinned as Featured Feedback on their shop. */
export async function listMerchantFeaturedReviews(
  id: string,
  opts?: { limit?: number },
): Promise<MerchantReview[]> {
  const res = await api.get<{ reviews: MerchantReview[] }>(`/merchants/${id}/featured-reviews`, {
    params: opts,
  });
  return res.data.reviews;
}

/** Reviews admin pinned as Featured Feedback on the Official Store. */
export async function listOfficialStoreFeaturedReviews(opts?: {
  limit?: number;
}): Promise<MerchantReview[]> {
  const res = await api.get<{ reviews: MerchantReview[] }>(
    '/storefront/official-store/featured-reviews',
    { params: opts },
  );
  return res.data.reviews;
}

export type MerchantTopSeller = { productId: string; name: string; unitsSold: number };
export async function listMerchantTopSelling(
  id: string,
  opts?: { limit?: number },
): Promise<MerchantTopSeller[]> {
  const res = await api.get<{ topSelling: MerchantTopSeller[] }>(`/merchants/${id}/top-selling`, {
    params: opts,
  });
  return res.data.topSelling;
}
export async function getMerchantFollowState(id: string): Promise<{ following: boolean }> {
  const res = await api.get<{ following: boolean }>(`/merchants/${id}/follow-state`);
  return res.data;
}
export async function followMerchant(
  id: string,
): Promise<{ following: boolean; followers: number }> {
  const res = await api.post<{ following: boolean; followers: number }>(`/merchants/${id}/follow`);
  return res.data;
}
export async function unfollowMerchant(
  id: string,
): Promise<{ following: boolean; followers: number }> {
  const res = await api.delete<{ following: boolean; followers: number }>(
    `/merchants/${id}/follow`,
  );
  return res.data;
}

// ── Official (house) store follow ──
export async function getOfficialStoreFollowState(): Promise<{
  following: boolean;
  followers: number;
}> {
  const res = await api.get<{ following: boolean; followers: number }>(
    '/storefront/official-store/follow-state',
  );
  return res.data;
}
export type OfficialStoreStats = {
  productCount: number;
  rating: { average: number; count: number };
  followers: number;
  transactions: number;
};
export async function getOfficialStoreStats(): Promise<OfficialStoreStats> {
  const res = await api.get<OfficialStoreStats>('/storefront/official-store/stats');
  return res.data;
}
export async function followOfficialStore(): Promise<{ following: boolean; followers: number }> {
  const res = await api.post<{ following: boolean; followers: number }>(
    '/storefront/official-store/follow',
  );
  return res.data;
}
export async function unfollowOfficialStore(): Promise<{ following: boolean; followers: number }> {
  const res = await api.delete<{ following: boolean; followers: number }>(
    '/storefront/official-store/follow',
  );
  return res.data;
}

export type StorefrontBranch = {
  id: string;
  code: string;
  name: string;
  address: Record<string, string | undefined> | null;
};

export async function listStorefrontBranches(): Promise<StorefrontBranch[]> {
  const res = await api.get<StorefrontBranch[]>('/storefront/branches');
  return res.data;
}

export type ShippingQuote = {
  courierId: string;
  courierLabel: string;
  cod: boolean;
  branchOnly: boolean;
  packageKey: string | null;
  packageLabel: string | null;
  fee: string;
  available: boolean;
  reason?: string;
  /** Free-shipping tag: every item in this group ships at no charge. */
  freeShipping?: boolean;
  /**
   * The courier defers its fee to an admin: `fee` is '0.0000' and is NOT what
   * the buyer pays. Render "to be advised", never "FREE", and keep it out of
   * the order total.
   */
  manual?: boolean;
};

export type ShippingQuoteResponse = {
  available: boolean;
  reason?: string;
  origin?: string;
  destination?: string;
  quotes: ShippingQuote[];
  /** The courier the member last shipped with — the checkout pre-selects it when
   *  it's still among the available quotes. null = no prior order with a courier. */
  lastCourierId?: string | null;
};

export async function getShippingQuote(args: {
  // Exactly one origin: a house POO branch OR a merchant's pickup (its shop ships
  // from the merchant's address). Merchant legs of a mixed cart use merchantUserId.
  branchId?: string;
  merchantUserId?: string;
  // Pass `variantId` so the parcel is packed from the VARIATION's dimensions and
  // its `free_shipping` override, not the parent product's.
  items: Array<{ productId: string; variantId?: string; qty: number }>;
  address?: Record<string, unknown>;
}): Promise<ShippingQuoteResponse> {
  const res = await api.post<ShippingQuoteResponse>('/storefront/shipping/quote', args);
  return res.data;
}

/** Public courier-logo endpoint (302-redirects to a presigned image) for <img src>. */
export function courierLogoUrl(courierId: string): string {
  return `${BASE_URL}/shipping/courier-logo/${encodeURIComponent(courierId)}`;
}

/** Public ecom payment-method logo endpoint (302-redirects to a presigned image). */
export function ecomPaymentMethodLogoUrl(code: string): string {
  return `${BASE_URL}/storefront/payment-method-logo/${encodeURIComponent(code)}`;
}

/** Public ecom payment-method QR endpoint (302-redirects to a presigned image). */
export function ecomPaymentMethodQrUrl(code: string): string {
  return `${BASE_URL}/storefront/payment-method-qr/${encodeURIComponent(code)}`;
}

// ── Orders ─────────────────────────────────────────────────────────────

/**
 * `GET /orders` enriches each row with a compact item summary + the fulfillment
 * branch so the Orders list can render rich cards without an N+1 fetch. Kept
 * separate from `OrderRow` so it doesn't clash with `OrderDetail`'s richer
 * `items` shape.
 */

export async function uploadManualPayment(args: {
  orderId: string;
  method: string;
  amount: string;
  currency: string;
  reference?: string;
  file: File;
}): Promise<{ id: string; status: string; scanStatus: string | null }> {
  const form = new FormData();
  form.append('orderId', args.orderId);
  form.append('method', args.method);
  form.append('amount', args.amount);
  form.append('currency', args.currency);
  if (args.reference) form.append('reference', args.reference);
  form.append('file', args.file);
  const res = await api.post<{ id: string; status: string; scanStatus: string | null }>(
    '/payments/manual',
    form,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return res.data;
}

/**
 * Single payment for a mixed cart: one proof + the summed total covers every
 * order in the checkout group. The server creates one manual_payments row per
 * order (so admin still approves each shop separately).
 */
export async function uploadManualPaymentGroup(args: {
  checkoutGroupId: string;
  method: string;
  amount: string;
  currency: string;
  reference?: string;
  file: File;
}): Promise<{ count: number; orderIds: string[] }> {
  const form = new FormData();
  form.append('checkoutGroupId', args.checkoutGroupId);
  form.append('method', args.method);
  form.append('amount', args.amount);
  form.append('currency', args.currency);
  if (args.reference) form.append('reference', args.reference);
  form.append('file', args.file);
  const res = await api.post<{ count: number; orderIds: string[] }>('/payments/manual', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return res.data;
}

export type OrderGroup = {
  checkoutGroupId: string;
  orders: Array<{
    id: string;
    code: string;
    status: string;
    total: string;
    merchantUserId: string | null;
  }>;
  payableTotal: string;
  allPaid: boolean;
};

export async function getOrderGroup(groupId: string): Promise<OrderGroup> {
  const res = await api.get<OrderGroup>(`/orders/group/${groupId}`);
  return res.data;
}

export async function listMyOrders(
  slotId?: string,
  opts?: { statuses?: string[]; limit?: number },
): Promise<OrderListRow[]> {
  const params: Record<string, string | number> = {};
  if (slotId) params.slotId = slotId;
  if (opts?.statuses && opts.statuses.length > 0) params.status = opts.statuses.join(',');
  if (opts?.limit) params.limit = opts.limit;
  const res = await api.get<OrderListRow[]>('/orders', { params });
  return res.data;
}

export type OrderDetail = OrderRow & {
  trackingNumber?: string | null;
  trackingUrl?: string | null;
  /** See `OrderListRow.cancellable` — same server-decided field. */
  cancellable?: boolean;
  cancelBlock?: string | null;
  fulfillmentMode?: 'ship' | 'branch_pickup';
  shipFromBranch?: { id: string; code: string; name: string } | null;
  items: Array<{
    id: string;
    productId: string;
    qty: number;
    unitPrice: string;
    unitPriceBase: string;
    appliedDiscountPct: string;
    unitPv: string;
    productName?: string | null;
    productSku?: string | null;
    productImages?: string[] | null;
    variantId?: string | null;
    variantName?: string | null;
  }>;
  statusHistory: Array<{ status: string; note: string | null; changedAt: string }>;
  /** True when a manual payment was submitted and is awaiting admin verification.
   *  OPTIONAL on purpose: a cached SPA bundle can outlive a deploy, and
   *  `undefined` should degrade to the old in-memory-only behaviour rather than
   *  be a type lie. */
  paymentPending?: boolean;
  /**
   * The latest online-payment attempt (status + provider only). A failed or
   * expired attempt leaves the order payable at `to_pay`, so only this says
   * it did not go through. Optional for the same cached-bundle reason.
   */
  gatewayPayment?: { provider: string; status: string; updatedAt: string } | null;
};

export async function getOrder(id: string): Promise<OrderDetail> {
  const res = await api.get<OrderDetail>(`/orders/${id}`);
  return res.data;
}

export async function markOrderReceived(id: string): Promise<{ id: string; status: string }> {
  const res = await api.post<{ id: string; status: string }>(`/orders/${id}/receive`, {});
  return res.data;
}

/**
 * Cancel an unpaid order. When the order belongs to a checkout group the server
 * cancels the WHOLE group (one payment covers it), so the response lists every
 * order that actually moved.
 */
export async function cancelMyOrder(
  id: string,
  reason?: string,
): Promise<{ cancelled: string[]; groupId: string | null }> {
  const res = await api.post<{ cancelled: string[]; groupId: string | null }>(
    `/orders/${id}/cancel`,
    reason ? { reason } : {},
  );
  return res.data;
}

export type ProductReview = {
  id: string;
  orderItemId: string;
  productId: string;
  userId: string;
  rating: number;
  comment: string | null;
  photoR2Key: string | null;
  photoR2Keys: string[] | null;
  aspectTags: string[] | null;
  createdAt: string;
};

export async function listOrderReviews(orderId: string): Promise<{ reviews: ProductReview[] }> {
  const res = await api.get<{ reviews: ProductReview[] }>(`/orders/${orderId}/reviews`);
  return res.data;
}

export async function getReviewPhotoUrls(
  orderId: string,
  reviewId: string,
): Promise<{ urls: string[] }> {
  const res = await api.get<{ urls: string[]; expiresInSeconds: number }>(
    `/orders/${orderId}/reviews/${reviewId}/photo-urls`,
  );
  return { urls: res.data.urls };
}

export async function submitProductReview(args: {
  orderId: string;
  orderItemId: string;
  rating: number;
  comment?: string;
  photos?: File[];
  aspectTags?: string[];
}): Promise<ProductReview> {
  const form = new FormData();
  form.append('rating', String(args.rating));
  if (args.comment) form.append('comment', args.comment);
  if (args.aspectTags?.length) form.append('aspectTags', JSON.stringify(args.aspectTags));
  for (const photo of args.photos ?? []) form.append('file', photo);
  const res = await api.post<ProductReview>(
    `/orders/${args.orderId}/items/${args.orderItemId}/review`,
    form,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return res.data;
}

export async function createOrder(args: {
  type: 'ecom';
  // `variantId` is REQUIRED for a product that has variations — the server
  // prices and stock-checks the parent when it's missing, which reads the wrong
  // inventory bucket (see `variant_required` in services/orders.ts).
  // `autoRenew` marks a creator subscription plan line the buyer wants renewed
  // from their wallet; the server honours it only on a cash wallet checkout.
  items: Array<{ productId: string; variantId?: string; qty: number; autoRenew?: boolean }>;
  branchId?: string;
  notes?: string;
  courierId?: string;
  shippingFee?: string;
  shipToAddress?: Record<string, unknown>;
  /** 'ship' (courier, default) or 'branch_pickup' (collect at `branchId`). */
  fulfillmentMode?: 'ship' | 'branch_pickup';
  /** BF4: pay in 'cash' (default), 'gc', or 'coin'. Non-cash settles instantly. */
  currency?: 'cash' | 'gc' | 'coin';
  /**
   * Admin-managed ecom payment method code. When present the backend ignores
   * `currency` and resolves currency + settlement (wallet vs manual proof
   * upload) from `tenant_config.features.ecom_payment_methods`.
   */
  paymentMethodCode?: string;
  /** Coupon code (cash orders only). Discounts the eligible product lines. */
  couponCode?: string;
  /** Single-payment correlation shared with sibling merchant orders. */
  checkoutGroupId?: string;
  /** The member's selected active slot — attributes comp/PV + gc/coin debit. */
  buyerSlotId?: string;
}): Promise<OrderRow> {
  const res = await api.post<OrderRow>('/orders', args);
  return res.data;
}

/**
 * Create a merchant sub-order (marketplace). Cash-only at sticker/variant price;
 * the order is created `pending` and settled afterward via the order's
 * manual-payment proof flow. A mixed cart places one of these per merchant in
 * addition to the house `createOrder` call.
 */
export async function createMerchantOrder(args: {
  merchantUserId: string;
  items: Array<{ productId: string; variantId?: string; qty: number }>;
  shipToAddress?: Record<string, unknown>;
  shipToRegionCode?: string;
  courierId?: string;
  /** Manual or cash-wallet method chosen at checkout; omitted = first manual. */
  paymentMethodCode?: string;
  /** Which slot's wallet pays (per-slot balances); omitted = earliest active. */
  buyerSlotId?: string;
  /** Single-payment correlation shared with the house + sibling merchant orders. */
  checkoutGroupId?: string;
  notes?: string;
}): Promise<OrderRow> {
  const res = await api.post<OrderRow>('/merchant-orders', args);
  return res.data;
}

/**
 * Dropshipping Income: place a retail order through a member's per-product
 * replicated link. The caller is an authenticated buyer (a lead just signed up
 * via the link, or an existing member); `ref` is the link's referral value
 * (plaintext code or encrypted `e1.` token). The sharer earns the SRP margin;
 * the order is created `pending` and settled via the manual-payment proof flow.
 */
export async function createDropshipOrder(args: {
  items: Array<{ productId: string; variantId?: string; qty: number }>;
  ref: string;
  shipToAddress?: Record<string, unknown>;
  notes?: string;
  /** Manual payment method chosen at checkout; server falls back to first manual. */
  paymentMethodCode?: string;
  /** Single-payment correlation shared with sibling merchant orders. */
  checkoutGroupId?: string;
}): Promise<OrderRow> {
  const res = await api.post<OrderRow>('/storefront/dropship-orders', args);
  return res.data;
}

/**
 * Validate a coupon against the cart before checkout. Returns the per-line
 * coupon discount + post-coupon total, or `{ valid:false, error }` with a
 * reason code (coupon_expired, coupon_not_applicable, …).
 */
/**
 * `target` says WHICH order the code will ride. A cart can split into a house
 * order plus one sub-order per partner, and a coupon can only ride one of them:
 * checkout sends the code to that order and to no other, so the discount
 * charged is the discount quoted.
 */
export type CouponTarget = { kind: 'house' } | { kind: 'creator'; creatorUserId: string };

export type CouponValidation =
  | {
      valid: true;
      couponCode: string | null;
      couponTotal: string;
      total: string;
      target?: CouponTarget;
    }
  | { valid: false; error: string; message?: string };

export async function validateCoupon(args: {
  code: string;
  // Variation-aware: the preview must price the VARIANT, or the quoted discount
  // is computed off the parent's price and won't match what checkout charges.
  items: Array<{ productId: string; variantId?: string; qty: number }>;
  /** Quote against the member's selected active slot (matches checkout pricing). */
  buyerSlotId?: string;
}): Promise<CouponValidation> {
  try {
    const res = await api.post<CouponValidation>('/storefront/coupons/validate', args);
    return res.data;
  } catch (e: unknown) {
    const data = (e as { response?: { data?: { error?: string; message?: string } } })?.response
      ?.data;
    return { valid: false, error: data?.error ?? 'coupon_invalid', message: data?.message };
  }
}

// ── Ecom payment methods (member checkout) ────────────────────────────────
export type EcomPaymentMethodOption = {
  code: string;
  label: string;
  currency: 'cash' | 'gc' | 'coin';
  settlement: 'wallet' | 'manual' | 'gateway' | 'cod_manual';
  accountLabel: string | null;
  instructions: string | null;
  /** Presence flag — when set, fetch the image via `ecomPaymentMethodLogoUrl(code)`. */
  logoKey: string | null;
  /** Presence flag — when set, fetch the scannable QR via `ecomPaymentMethodQrUrl(code)`. */
  qrKey: string | null;
  /** For settlement='gateway': the online provider (dragonpay/ipay88/stub). */
  gatewayProvider?: string | null;
  /** For settlement='cod_manual': the admin default courier the server auto-assigns
   *  when a COD order arrives with no courier. Lets checkout pre-price COD shipping. */
  defaultCourierId?: string | null;
  /** Whether the tenant offers this method on an ALL-VIRTUAL order. Absent on an
   *  older API build, which is why callers must treat undefined as allowed —
   *  the registry field is opt-OUT and `POST /orders` is the real gate. */
  virtualEnabled?: boolean;
  /** Decimal string for wallet methods; null for manual or when no active slot. */
  balance: string | null;
  /**
   * Paying a house subscription plan with this method starts a card
   * subscription that renews by itself (Stripe). Absent on an older API build.
   */
  renewsPlans?: boolean;
};

export type EcomPaymentMethodsResponse = {
  /** Active slot the wallet balances are read from; null if the member has none. */
  slotId: string | null;
  balances: { cash: string; gc: string; coin: string } | null;
  methods: EcomPaymentMethodOption[];
};

export async function getEcomPaymentMethods(
  slotId?: string | null,
): Promise<EcomPaymentMethodsResponse> {
  const res = await api.get<EcomPaymentMethodsResponse>('/storefront/payment-methods', {
    params: slotId ? { slotId } : undefined,
  });
  return res.data;
}

// ── Online payment gateways (Dragonpay / iPay88) ────────────────────────────
/** Discriminated handoff returned by `initiateGateway` — how to send the buyer to pay. */
export type GatewayHandoff =
  | { kind: 'redirect'; url: string }
  | { kind: 'form_post'; actionUrl: string; fields: Record<string, string> }
  | { kind: 'qr'; image: string; pollUrl: string };

/**
 * Start an online-gateway payment for a placed order (or a mixed-cart checkout
 * group). `procId` is the buyer's chosen channel (Dragonpay ProcId / iPay88
 * PaymentId); omit to use the method's configured default. Returns the handoff
 * the SPA must execute to send the buyer to pay.
 */
export async function initiateGateway(args: {
  orderId?: string;
  checkoutGroupId?: string;
  procId?: string;
}): Promise<{ handoff: GatewayHandoff; txnId: string }> {
  const res = await api.post<{ handoff: GatewayHandoff; txnId: string }>(
    '/payments/gateway/initiate',
    args,
  );
  return res.data;
}

export type DragonpayChannel = { procId: string; name: string; logo: string | null };

/** The merchant's enabled Dragonpay channels for the buyer's checkout picker. */
export async function getDragonpayChannels(): Promise<DragonpayChannel[]> {
  const res = await api.get<{ channels: DragonpayChannel[] }>(
    '/payments/gateway/dragonpay/channels',
  );
  return res.data.channels;
}

/**
 * Execute a gateway handoff: navigate the browser to pay. `redirect` is a plain
 * location change; `form_post` builds a hidden auto-submitting form (iPay88
 * `entry.asp`). The browser leaves the SPA and returns via the gateway's
 * return URL to the order result page.
 */
export function performGatewayHandoff(handoff: GatewayHandoff): void {
  if (handoff.kind === 'redirect' || handoff.kind === 'qr') {
    window.location.assign(handoff.kind === 'redirect' ? handoff.url : handoff.pollUrl);
    return;
  }
  // form_post: POST the signed fields to the gateway via an auto-submitting form.
  const form = document.createElement('form');
  form.method = 'POST';
  form.action = handoff.actionUrl;
  form.style.display = 'none';
  for (const [name, value] of Object.entries(handoff.fields)) {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = value;
    form.appendChild(input);
  }
  document.body.appendChild(form);
  form.submit();
}

/** Public (no-auth) enabled payment methods for the storefront "Payment" info
 * page — display fields only, no wallet balances. */
export type PublicPaymentMethod = {
  code: string;
  label: string;
  currency: 'cash' | 'gc' | 'coin';
  settlement: 'wallet' | 'manual';
  accountLabel: string | null;
  instructions: string | null;
  logoKey: string | null;
  qrKey: string | null;
};
export async function getPublicPaymentMethods(): Promise<{ methods: PublicPaymentMethod[] }> {
  const res = await api.get<{ methods: PublicPaymentMethod[] }>(
    '/storefront/payment-methods/public',
  );
  return res.data;
}

/** Public enabled couriers + their tracking-page link, for the Track Order page. */
export type StorefrontCourier = {
  id: string;
  label: string;
  logoKey: string | null;
  trackingUrl: string | null;
};
export async function getStorefrontCouriers(): Promise<{ couriers: StorefrontCourier[] }> {
  const res = await api.get<{ couriers: StorefrontCourier[] }>('/storefront/couriers');
  return res.data;
}

/** Public feedback to the marketplace owner. Works logged-out; the server stamps
 * the user id when a session is present. */
export type FeedbackInput = {
  category: 'suggestion' | 'complaint' | 'praise' | 'bug' | 'other';
  message: string;
  name?: string;
  email?: string;
  subject?: string;
  rating?: number;
  pageUrl?: string;
};
export async function submitFeedback(input: FeedbackInput): Promise<{ ok: true }> {
  const res = await api.post<{ ok: true }>('/storefront/feedback', input);
  return res.data;
}

// ── Payouts ────────────────────────────────────────────────────────────

export async function listPayoutMethods(): Promise<PayoutMethodOption[]> {
  const res = await api.get<PayoutMethodOption[]>('/payouts/methods');
  return res.data;
}

/**
 * Saved payout QR codes — the QR image a payout should be sent to, one per
 * payout method. `methodCode` matches a `listPayoutMethods()` entry; there is
 * no fixed list of QR "types" (GCash / QRPH / …) — they come from whatever the
 * admin configured under Fees & Tax.
 */
export type PayoutQr = {
  id: string;
  methodCode: string;
  label: string | null;
  /** Short-lived presigned URL, safe to drop straight into an <img src>. */
  qrUrl: string;
  updatedAt: string;
};

export async function listPayoutQrs(): Promise<PayoutQr[]> {
  const res = await api.get<{ items: PayoutQr[] }>('/me/payout-qrs');
  return res.data.items;
}

/** Upload or replace the QR for one method (upserts on method). */
export async function uploadPayoutQr(args: {
  file: File;
  methodCode: string;
  label?: string;
}): Promise<{ id: string; methodCode: string; label: string | null; qrUrl: string }> {
  const form = new FormData();
  form.append('methodCode', args.methodCode);
  if (args.label) form.append('label', args.label);
  form.append('file', args.file);
  const res = await api.post<{
    id: string;
    methodCode: string;
    label: string | null;
    qrUrl: string;
  }>('/me/payout-qrs', form);
  return res.data;
}

export async function deletePayoutQr(id: string): Promise<{ ok: boolean }> {
  const res = await api.delete<{ ok: boolean }>(`/me/payout-qrs/${id}`);
  return res.data;
}

export async function getCashoutWindow(): Promise<CashoutWindow> {
  const res = await api.get<CashoutWindow>('/payouts/window');
  return res.data;
}

/**
 * One deduction, in the order it was applied. The shared WIRE shape: distinct
 * from `payout-deductions.ts`'s `PayoutBreakdownStep`, which is the Decimal
 * compute shape. Aliased rather than renamed so this SPA's call sites are
 * unchanged.
 */
export type PayoutBreakdownStep = PayoutBreakdownStepWire;

export async function previewPayout(args: {
  slotId: string;
  amount: string;
  method: string;
}): Promise<PayoutPreview> {
  const res = await api.post<PayoutPreview>('/payouts/requests/preview', args);
  return res.data;
}

export async function listMyPayouts(args?: {
  slotId?: string;
  /** Registry method code — options come from `listPayoutMethods`, never hardcoded. */
  method?: string;
  /** ISO datetimes bounding `requestedAt`. */
  from?: string;
  to?: string;
  /** Newest-first is the server default. */
  dir?: 'asc' | 'desc';
  page?: number;
  pageSize?: number;
}): Promise<PayoutRequest[]> {
  const params: Record<string, string | number> = {};
  if (args?.slotId) params.slotId = args.slotId;
  if (args?.method) params.method = args.method;
  if (args?.from) params.from = args.from;
  if (args?.to) params.to = args.to;
  if (args?.dir) params.dir = args.dir;
  if (args?.page) params.page = args.page;
  if (args?.pageSize) params.pageSize = args.pageSize;
  const res = await api.get<PayoutRequest[]>('/payouts/requests', { params });
  return res.data;
}

export async function createPayoutRequest(args: {
  slotId: string;
  amount: string;
  method: string;
  payload?: Record<string, unknown>;
}): Promise<PayoutRequest> {
  const res = await api.post<PayoutRequest>('/payouts/requests', args);
  return res.data;
}

export async function cancelPayoutRequest(id: string): Promise<void> {
  await api.post(`/payouts/requests/${id}/cancel`);
}

// ── Leads ──────────────────────────────────────────────────────────────
export type Lead = {
  id: string;
  email: string;
  fullName: string | null;
  signedUpAt: string;
  hasActiveSlot: boolean;
  /** The sponsor slot's code this lead registered under, or null when it wasn't
   *  captured (legacy leads, or owner-level codes that resolved to no slot). */
  slotCode: string | null;
};

export async function getMyLeads(): Promise<Lead[]> {
  const res = await api.get<Lead[]>('/me/leads');
  return res.data;
}

// ── Profile ────────────────────────────────────────────────────────────
export type MemberAddress = {
  line1?: string;
  line2?: string;
  barangay?: string;
  city?: string;
  province?: string;
  region?: string;
  regionCode?: string;
  // JM-EX4: stable PSGC-style codes from `phil-reg-prov-mun-brgy`. Optional
  // so addresses created before the cascading-dropdown ship keep working
  // (name-only). New entries store both code + human name.
  provinceCode?: string;
  cityCode?: string;
  barangayCode?: string;
  postalCode?: string;
  contactNo?: string;
};

export type MeProfile = {
  id: string;
  email: string;
  username?: string | null;
  fullName: string | null;
  firstName: string | null;
  middleName: string | null;
  lastName: string | null;
  phone: string | null;
  /** Replicated-link referral code. Present on /me so the Invite page can read it directly. */
  referralCode?: string | null;
  /** Slot code the Vault redeem form pre-fills as the sponsor; null only on bootstrap failure. */
  defaultSponsorSlotCode?: string | null;
  /** True when the member signed up via a referral/product link — locks the redeem sponsor field. */
  referredViaLink?: boolean;
  /** Per-package link: the kit product ("package") this lead arrived through, or
   *  null. LEGACY (retired `?pkg=` capture) — kept for wire-compat; the dashboard
   *  now scopes on `registeredLinkKitIds`. */
  registeredUnderKitProductId?: string | null;
  /** Replicated-link packages: the activation kits this lead is scoped to (their
   *  campaign page's tagged kits). null = no restriction / feature off. The
   *  dashboard filters its "Pick your activation kit" list to this set. */
  registeredLinkKitIds?: string[] | null;
  /** True when this member signed up through a CAMPAIGN / replicated page rather
   *  than a plain referral link. Only the unpaid-order lock reads it. Optional
   *  because an older API build doesn't send it — treat undefined as false, i.e.
   *  don't lock someone the server can't confirm came through a campaign. */
  registeredViaCampaign?: boolean;
  /** Admin authoritative sponsor override active — locks (and may hide) the redeem sponsor field. */
  sponsorOverridden?: boolean;
  /** Server-authoritative: may the member edit the sponsor at their next activation
   *  (per the tenant editability toggles)? When false, the redeem sponsor field is
   *  locked to `defaultSponsorSlotCode`. Undefined (older API) = treat as editable. */
  sponsorEditable?: boolean;
  address: MemberAddress | null;
  // BF1: true when the tenant bumped Terms past the version the member accepted.
  termsReacceptanceRequired?: boolean;
  termsVersionAccepted?: number | null;
  // F3: admin-set ledger display-name overrides (sparse; SPA falls back to
  // shared built-in defaults). Keyed by raw ledger kind / source_plan.
  ledgerLabels?: Record<string, string>;
  ledgerSourcePlanLabels?: Record<string, string>;
  tenantPolicy?: {
    member2faRequired?: boolean;
    requireTermsOnSignup?: boolean;
    currentTermsVersion?: number;
    // Comp plans the tenant does NOT run; a plan is enabled when absent. Lets
    // the dashboard show only the relevant standing metrics.
    disabledPlanCodes?: string[];
    coinLabel?: string;
    // What per-month PV the dashboard may show (off / ppv only / ppv + gpv).
    memberPvDisplay?: 'off' | 'ppv_only' | 'ppv_and_gpv';
    // Wallet top-up: gate the page + nav, populate the method dropdown, and let
    // the SPA preview the bonus from the same brackets the server credits with.
    walletTopupEnabled?: boolean;
    /** Each with the name of the gift check program it gives, if any (phase 5). */
    walletTopupBrackets?: Array<WalletTopupBracket & { voucherProgramName?: string | null }>;
    /** When on, an approved top-up to an in-debt slot pays its CD debt first. */
    cdTopupSettlesDebt?: boolean;
    /** How a bonus is handled on a debt-paying top-up (default principal_only). */
    cdTopupBonusTreatment?: 'principal_only' | 'include_cash_bonus' | 'no_bonus';
    /** Member can buy AI Assistant credits through the top-up flow. */
    aiCreditsForSale?: boolean;
    /** Peso price of one credit (null when not for sale). */
    aiCreditPrice?: string | null;
  };
};

export async function getMe(): Promise<MeProfile> {
  const res = await api.get<MeProfile>('/me');
  return res.data;
}

// ── Wallet top-up ────────────────────────────────────────────────────────

export async function listMyTopups(): Promise<MyTopup[]> {
  const res = await api.get<MyTopup[]>('/wallet/topups');
  return res.data;
}

export async function uploadTopup(args: {
  slotId: string;
  method: string;
  /** Pesos for a cash top-up; a COUNT of credits when buying AI Credits. */
  amount: string;
  /** Omit (or 'cash') to top up the wallet; 'ai_credit' buys assistant answers. */
  currency?: 'cash' | 'ai_credit';
  reference?: string;
  file: File;
}): Promise<{ id: string; status: string }> {
  const form = new FormData();
  form.append('slotId', args.slotId);
  form.append('method', args.method);
  form.append('amount', args.amount);
  if (args.currency) form.append('currency', args.currency);
  if (args.reference) form.append('reference', args.reference);
  form.append('file', args.file);
  const res = await api.post<{ id: string; status: string }>('/wallet/topups', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return res.data;
}

// ── Auto-Ship ────────────────────────────────────────────────────────────
export type AutoshipItemInput = { productId: string; variantId?: string | null; qty: number };
export type AutoshipSubscription = {
  id: string;
  slotId: string;
  currency: 'cash' | 'gc' | 'coin';
  dayOfMonth: number;
  nextRunDate: string;
  status: 'active' | 'paused';
  fulfillmentMode: 'ship' | 'branch_pickup';
  items: AutoshipItemInput[];
  runs: { runMonth: string; status: string; orderId: string | null; createdAt: string }[];
};

export async function listMyAutoship(): Promise<AutoshipSubscription[]> {
  const res = await api.get<AutoshipSubscription[]>('/me/autoship');
  return res.data;
}

export async function createAutoship(args: {
  slotId: string;
  currency: 'cash' | 'gc' | 'coin';
  dayOfMonth: number;
  fulfillmentMode: 'ship' | 'branch_pickup';
  shipToAddress?: Record<string, unknown> | null;
  items: AutoshipItemInput[];
}): Promise<{ id: string }> {
  const res = await api.post<{ id: string }>('/me/autoship', args);
  return res.data;
}

export async function updateAutoship(
  id: string,
  patch: { status?: 'active' | 'paused'; dayOfMonth?: number; items?: AutoshipItemInput[] },
): Promise<void> {
  await api.patch(`/me/autoship/${id}`, patch);
}

export async function deleteAutoship(id: string): Promise<void> {
  await api.delete(`/me/autoship/${id}`);
}

export async function patchMe(args: {
  firstName?: string;
  middleName?: string | null;
  lastName?: string;
  phone?: string | null;
  address?: MemberAddress | null;
  /** `YYYY-MM-DD`; null clears it while the tenant's field is optional. */
  birthDate?: string | null;
}): Promise<{
  id: string;
  fullName: string | null;
  firstName: string | null;
  middleName: string | null;
  lastName: string | null;
  phone: string | null;
  address: MemberAddress | null;
  birthDate: string | null;
}> {
  const res = await api.patch('/me', args);
  return res.data;
}

export async function changePassword(args: {
  currentPassword: string;
  newPassword: string;
}): Promise<void> {
  await api.post('/me/password', args);
}

// ── Phase 8: privacy & data (data export + account archival) ──────────────

/**
 * Pull the member's data export and hand it to the browser as a file.
 *
 * It can't be a plain link or `window.open`: the access token lives only in
 * memory (see the auth store), so an unauthenticated navigation would 401.
 * Fetch it as a blob with the interceptor's Authorization header on, then save
 * it locally. The server names the file in `content-disposition`; we honour
 * that name when it's there so the download matches the API's own contract.
 */
export async function downloadMyDataExport(): Promise<void> {
  const res = await api.get('/me/data-export', { responseType: 'blob' });
  const disposition = String(res.headers['content-disposition'] ?? '');
  const named = /filename="?([^";]+)"?/i.exec(disposition);
  downloadBlob(res.data as BlobPart, named?.[1] ?? 'my-data.json', 'application/json');
}

export type ArchiveRequest = {
  status: string;
  requestedAt: string;
  reason: string | null;
};

/** The caller's OPEN archival request, or null when they have none. */
/**
 * Bring a closed (deactivated) account back. Returns nothing: reactivating and
 * signing in are two steps, so every login rule — TOTP included — still applies
 * to the sign-in that follows.
 */
export async function reactivateAccount(input: { identifier: string; password: string }) {
  await api.post('/auth/reactivate', input);
}

export async function getMyArchiveRequest(): Promise<ArchiveRequest | null> {
  const res = await api.get<ArchiveRequest | null>('/me/archive-request');
  return res.data;
}

export async function requestMyArchive(input: {
  password: string;
  reason?: string;
}): Promise<{ id: string }> {
  const res = await api.post<{ ok: true; id: string }>('/me/archive-request', input);
  return { id: res.data.id };
}

export async function cancelMyArchiveRequest(): Promise<void> {
  await api.delete('/me/archive-request');
}

// ── Slot standing (MLM position) ─────────────────────────────────────────
export type SlotStanding = {
  membership: { code: string; name: string } | null;
  rank: { code: string } | null;
  pv: { personalPv: string; groupPv: string; periodCode: string } | null;
  /** The slot's sponsor (owner + slot code), or null for a root slot. */
  sponsor: { slotCode: string; ownerName: string | null } | null;
  /** Active binary variant; legacy-binary dashboard surfaces show only when 'legacy'. */
  binaryVariant: 'legacy' | 'teslab' | 'extreme' | null;
  binary: {
    leftPv: string;
    rightPv: string;
    leftCount: number;
    rightCount: number;
    pairsToday: number;
    earnedToday: string;
    flushedToday: string;
    capped: boolean;
    /** Per-day paid-pair count cap for the tier (0 = unlimited). */
    maxPairsPerDay: number;
    /** Lifetime cumulative paid pairs. */
    lifetimePaidPairs: number;
    /** Progress toward the next GC pair-drop milestone, or null when none configured. */
    nextGcMilestone: {
      n: number;
      gc: string;
      pairsInto: number;
      /** True when the Nth pair converts its pairing payout to GC (gc may be 0). */
      pairingToGc: boolean;
    } | null;
  } | null;
};

/** Cached server-side; safe to call on every dashboard load. */
export async function getSlotStanding(slotId: string): Promise<SlotStanding> {
  const res = await api.get<SlotStanding>(`/slots/${slotId}/standing`);
  return res.data;
}

// ── Binary points history (legacy binary; member dashboard panel) ─────────
export type BinaryPointRow = {
  id: string;
  side: string;
  delta: string;
  balanceBefore: string | null;
  balanceAfter: string | null;
  kind: string;
  fromSlotCode: string | null;
  createdAt: string;
};

export async function getBinaryPointsHistory(
  slotId: string,
  page = 1,
  pageSize = 10,
): Promise<{ rows: BinaryPointRow[]; hasMore: boolean }> {
  const res = await api.get<{ rows: BinaryPointRow[]; hasMore: boolean }>(
    `/slots/${slotId}/binary-points-history`,
    { params: { page, pageSize } },
  );
  return res.data;
}

/** Admin-style pivoted Binary History row (one per placement event, both legs). */
export type BinaryHistoryRow = {
  sourceEventId: string;
  createdAt: string;
  kinds: string[];
  causeSlotId: string | null;
  causeSlotCode: string | null;
  causeOwnerName: string | null;
  beforeLeft: string | null;
  beforeRight: string | null;
  addLeft: string | null;
  addRight: string | null;
  afterLeft: string | null;
  afterRight: string | null;
  amount: string;
  gc: string;
  flushout: boolean;
  flushoutIncome: string;
  /** Paid pairs formed by this event. */
  pairs: number;
  /** Lifetime paid-pair count after this event (incl. legacy carry-over). */
  lifetimePairs: number;
};

export async function getBinaryHistory(slotId: string): Promise<BinaryHistoryRow[]> {
  const res = await api.get<BinaryHistoryRow[]>(`/slots/${slotId}/binary-history`);
  return res.data;
}

// ── Extreme Binary side preference (single-side spillover) ─────────────────
/**
 * The slot's Extreme Binary spillover side + the directs-gate status. A member's
 * recruits spill straight down THAT slot's chosen L/R side; the side may only be
 * changed once `directs >= requirement` (surfaced as `canChange`).
 */
export type BinaryPreferenceStatus = {
  side: 'L' | 'R';
  directs: number;
  requirement: number;
  canChange: boolean;
};

/** Read the slot's spillover side + directs-gate status. Owner or admin. */
export async function getBinaryPreference(slotId: string): Promise<BinaryPreferenceStatus> {
  const res = await api.get<BinaryPreferenceStatus>(`/slots/${slotId}/binary-preference`);
  return res.data;
}

/**
 * Change the slot's spillover side. Affects FUTURE placements only — it never
 * moves already-placed downlines. 409 `binary_preference_locked` when the
 * directs gate isn't met.
 */
export async function setBinaryPreference(
  slotId: string,
  side: 'L' | 'R',
): Promise<BinaryPreferenceStatus> {
  const res = await api.patch<BinaryPreferenceStatus>(`/slots/${slotId}/binary-preference`, {
    side,
  });
  return res.data;
}

// ── Earnings breakdown (per-plan lifetime credits) ───────────────────────
export type EarningsBreakdownEntry = {
  /** Bucket key: the sourcePlan, or a sub-bucket key (e.g. the mentor rider). */
  key: string;
  sourcePlan: string;
  /** Ledger kind for sub-bucket rows (splits a rider from its host plan); null for plain plan rows. */
  kind: string | null;
  /** Give Me per-setting split: the `metadata.setting_label` this tile covers; null otherwise. */
  settingLabel: string | null;
  /** Display label, already resolved (admin override → default → humanized). */
  label: string;
  /** 'cash' | 'gc' | 'coin' | 'token' */
  currencyKind: string;
  totalCredit: string;
  entryCount: number;
  lastAt: string | null;
};

/** Per-plan earning cards for the member dashboard, enabled-plan-filtered and
 *  admin-ordered server-side. Returns one entry per (bucket key, currency). */
export async function getEarningsBreakdown(slotId: string): Promise<EarningsBreakdownEntry[]> {
  const res = await api.get<{ rows: EarningsBreakdownEntry[] }>(
    `/slots/${slotId}/earnings-breakdown`,
  );
  return res.data.rows;
}

/** A requirement the admin can hide from the member tracker, per rank. */
export type RankRequirementKey = 'ppv' | 'gpv' | 'legs' | 'directs' | 'total_earned';

export type RankProgress = {
  currentRankCode: string | null;
  currentRankName: string | null;
  /** API-relative (`/ranks/<code>/icon`) — resolve through `apiPathUrl`. */
  currentRankIconUrl: string | null;
  currentPpv: string;
  currentGpv: string;
  /** Slot's cumulative cash Total Earned (lifetime + legacy offset). */
  currentTotalEarned: string;
  activeDirects: number;
  nextRank: {
    code: string;
    name: string | null;
    description: string | null;
    iconUrl: string | null;
    minPpv: string;
    minGpv: string;
    minLegs: number;
    minLegRank: string | null;
    qualifyingLegs: number;
    minDirects: number;
    minTotalEarned: string;
    trackerHidden: RankRequirementKey[];
  } | null;
};

export async function getRankProgress(
  slotId: string,
): Promise<{ stairstepActive: boolean; progress: RankProgress | null }> {
  const res = await api.get<{ stairstepActive: boolean; progress: RankProgress | null }>(
    `/slots/${slotId}/rank-progress`,
  );
  return res.data;
}

/** One "X/N to receive ₱reward" milestone bar for the Give Me Bonus counter. */
export type GiveMeProgressRow = {
  settingIndex: number;
  settingLabel: string | null;
  unified: boolean;
  directTierCode: string | null;
  directTierName: string | null;
  directsRequired: number;
  reward: string;
  count: number;
  progress: number;
  batchesEarned: number;
};

/** `planLabel` is the tenant's display name for the plan (the card heading) —
 *  resolved server-side from the ledger-label overrides and the GM Settings'
 *  own labels, so renaming the plan in EITHER admin surface moves the heading. */
export type GiveMeProgress = {
  planActive: boolean;
  planLabel: string;
  rows: GiveMeProgressRow[];
};

export async function getGiveMeProgress(slotId: string): Promise<GiveMeProgress> {
  const res = await api.get<GiveMeProgress>(`/slots/${slotId}/give-me-progress`);
  return res.data;
}

export type MonthlyPvBucket = { month: string; ppv: string; gpv: string | null };

export async function getMonthlyPv(
  slotId: string,
  months?: number,
): Promise<{ display: 'off' | 'ppv_only' | 'ppv_and_gpv'; buckets: MonthlyPvBucket[] }> {
  const res = await api.get<{
    display: 'off' | 'ppv_only' | 'ppv_and_gpv';
    buckets: MonthlyPvBucket[];
  }>(`/slots/${slotId}/pv-monthly`, { params: months ? { months } : undefined });
  return res.data;
}

export type UnplacedSlot = {
  slotId: string;
  slotCode: string;
  userEmail: string | null;
  userFullName: string | null;
  membershipCode: string | null;
  membershipName: string | null;
};

/** Slots in this slot's sponsor subtree that still need a binary seat. */
export async function getUnplacedDownline(slotId: string): Promise<UnplacedSlot[]> {
  const res = await api.get<{ parentSlotId: string; nodes: UnplacedSlot[] }>(
    `/slots/${slotId}/unplaced-downline`,
  );
  return res.data.nodes;
}

/** Place an unplaced slot at an exact L/R seat under a parent. */
export async function placeSlot(
  slotId: string,
  args: { parentSlotId: string; position: 'L' | 'R' },
): Promise<{ ok: true }> {
  const res = await api.post<{ ok: true }>(`/slots/${slotId}/place`, args);
  return res.data;
}

// ── Genealogy ──────────────────────────────────────────────────────────

export async function getGenealogy(args: {
  slotId: string;
  view: 'unilevel' | 'binary';
  depth?: number;
}): Promise<GenealogyResponse> {
  const res = await api.get<GenealogyResponse>(`/slots/${args.slotId}/genealogy`, {
    params: { view: args.view, depth: args.depth ?? 5 },
  });
  return res.data;
}

export async function searchGenealogy(args: {
  slotId: string;
  view: 'unilevel' | 'binary';
  q: string;
}): Promise<GenealogySearchResponse> {
  const res = await api.get<GenealogySearchResponse>(`/slots/${args.slotId}/genealogy/search`, {
    params: { view: args.view, q: args.q },
  });
  return res.data;
}

// ── Notifications (JM-EX9) ──────────────────────────────────────────────
export type NotificationRow = {
  id: string;
  kind: string;
  title: string;
  body: string;
  metadata: Record<string, unknown>;
  readAt: string | null;
  createdAt: string;
};

export type NotificationsPage = {
  items: NotificationRow[];
  nextCursor: string | null;
};

export async function listNotifications(args: {
  limit?: number;
  cursor?: string;
}): Promise<NotificationsPage> {
  const res = await api.get<NotificationsPage>('/me/notifications', { params: args });
  return res.data;
}

export async function getUnreadCount(): Promise<{ count: number }> {
  const res = await api.get<{ count: number }>('/me/notifications/unread-count');
  return res.data;
}

export type OrderActionSlotCounts = {
  toPay: number;
  toReceive: number;
  toReview: number;
  total: number;
};

export type OrderActionSummary = {
  // Whole-user aggregates (back-compat).
  toPay: number;
  toReceive: number;
  toReview: number;
  total: number;
  // Un-slotted orders (ecom/kit/pre-activation, buyer_slot_id IS NULL). Surfaced
  // under whichever slot is active, so the sidebar adds these to the active slot.
  unslotted: OrderActionSlotCounts;
  // Per buyer-slot breakdown, keyed by slot id.
  bySlot: Record<string, OrderActionSlotCounts>;
};

export async function getOrderActionSummary(): Promise<OrderActionSummary> {
  const res = await api.get<OrderActionSummary>('/me/orders/action-summary');
  return res.data;
}

export type ReferralCodeInfo = {
  code: string | null;
  /**
   * The value to embed in shareable links: the encrypted opaque token when
   * `replicated_link_ref_encrypted` is on, else the plaintext code. Always use
   * this (not `code`) when building a link.
   */
  shareRef: string | null;
  changesUsed: number;
  maxChanges: number;
  enabled: boolean;
  canChange: boolean;
};

export async function getReferralCode(): Promise<ReferralCodeInfo> {
  const res = await api.get<ReferralCodeInfo>('/me/referral-code');
  return res.data;
}

// Bugs 2 & 6: a member's active PAID slots, each with its own shareRef (the
// slot_code, encrypted per the tenant flag). Used to point a product/replicated
// link at a SPECIFIC slot so the right slot earns when the member has several.
export type SlotShareLink = {
  slotId: string;
  slotCode: string;
  membershipName: string;
  // The custom code tied to this slot, or null when none is set (slot_code used).
  customCode: string | null;
  shareRef: string;
  /**
   * A free slot CAN refer (its referrals join under it, feeding the upline), but
   * a store link pointed at one is rejected at checkout with
   * `referrer_slot_not_earning` — so the picker marks it.
   */
  slotType: 'paid' | 'free' | 'cd' | 'half_cd';
};

export async function getSlotShareLinks(): Promise<SlotShareLink[]> {
  const res = await api.get<SlotShareLink[]>('/me/slots/share-links');
  return res.data;
}

// Per-package replicated links: for the member's PRIMARY active slot, one link
// per activation kit ("package"). A prospect who opens it registers under this
// slot AND is offered ONLY that package on their dashboard. The SPA builds
// `/signup?ref=<shareRef>&pkg=<kitProductId>` per entry.
export type PackageLink = {
  kitProductId: string;
  kitName: string;
  membershipName: string | null;
  shareRef: string;
};
export type PackageLinksResponse = { shareRef: string | null; packages: PackageLink[] };

/**
 * `slotId` names which of the member's slots the package links should earn for.
 * Omitted (or unresolvable) falls back to the primary slot — the historical
 * behaviour, so a stale id degrades rather than breaking the page.
 */
export async function getPackageLinks(slotId?: string): Promise<PackageLinksResponse> {
  const res = await api.get<PackageLinksResponse>('/me/package-links', {
    params: slotId ? { slotId } : undefined,
  });
  return res.data;
}

// Set a custom referral code for ONE slot (used instead of its slot_code).
export async function changeReferralCode(args: {
  code: string;
  slotId: string;
}): Promise<{ code: string; slotId: string; changesUsed: number; maxChanges: number }> {
  const res = await api.patch<{
    code: string;
    slotId: string;
    changesUsed: number;
    maxChanges: number;
  }>('/me/referral-code', args);
  return res.data;
}

export async function markNotificationRead(id: string): Promise<{ id: string; readAt: string }> {
  const res = await api.post<{ id: string; readAt: string }>(`/me/notifications/${id}/read`);
  return res.data;
}

export async function markAllNotificationsRead(): Promise<{ marked: number }> {
  const res = await api.post<{ marked: number }>('/me/notifications/read-all');
  return res.data;
}

// ── Training Videos (member-facing) ────────────────────────────────────────
export type TrainingVideoCategory = {
  id: string;
  name: string;
  sortOrder: number;
  active: boolean;
};

export type TrainingVideo = {
  id: string;
  title: string;
  description: string | null;
  categoryId: string | null;
  videoUrl: string;
  thumbnailKey: string | null;
  sortOrder: number;
  active: boolean;
};

export async function listTrainingVideos(
  categoryId?: string,
): Promise<{ categories: TrainingVideoCategory[]; videos: TrainingVideo[] }> {
  const res = await api.get<{ categories: TrainingVideoCategory[]; videos: TrainingVideo[] }>(
    '/training-videos',
    { params: categoryId ? { category: categoryId } : undefined },
  );
  return res.data;
}

export function trainingVideoThumbnailUrl(id: string): string {
  return `${api.defaults.baseURL ?? ''}/training-videos/${id}/thumbnail`;
}

// ── Resources library ──────────────────────────────────────────────────────

export type ResourceCategory = {
  id: string;
  name: string;
  sortOrder: number;
  active: boolean;
};

/**
 * A member-visible resource. `fileKey` is deliberately absent — the server never
 * sends the storage key; files are reached through the view/download routes,
 * which mint a short-lived presigned URL.
 */
export type MemberResource = {
  id: string;
  title: string;
  description: string | null;
  categoryId: string | null;
  kind: 'file' | 'link';
  originalFilename: string | null;
  mimeType: string | null;
  sizeBytes: number | null;
  linkUrl: string | null;
  sortOrder: number;
};

export async function listResources(
  categoryId?: string,
): Promise<{ categories: ResourceCategory[]; resources: MemberResource[] }> {
  const res = await api.get<{ categories: ResourceCategory[]; resources: MemberResource[] }>(
    '/resources',
    { params: categoryId ? { category: categoryId } : undefined },
  );
  return res.data;
}

/**
 * Ask the API for a short-lived presigned URL, then send the browser to it.
 *
 * Two steps rather than one because the URL is signed: the authenticated call
 * goes to our API, and the unauthenticated fetch of the bytes goes straight to
 * storage. The `Content-Disposition` the server signed in decides view vs
 * download, so the browser does the right thing either way.
 */
export async function resourceUrl(
  id: string,
  mode: 'view' | 'download',
): Promise<{ url: string; filename: string; expiresInSeconds: number }> {
  const res = await api.get<{ url: string; filename: string; expiresInSeconds: number }>(
    `/resources/${id}/${mode}`,
  );
  return res.data;
}

export async function openResource(id: string, mode: 'view' | 'download') {
  const { url } = await resourceUrl(id, mode);
  // In the packaged app a navigation to the storage host is handed to an
  // external browser (or dropped), so a "download" left the app entirely. Give
  // it to Android's DownloadManager instead.
  if (mode === 'download' && canDownloadUrlNative()) {
    if (await downloadUrlNative({ url })) return;
  }
  window.open(url, mode === 'view' ? '_blank' : '_self', 'noopener');
}

/**
 * A lesson attachment, by the same two-step as the resource library: ask our
 * API (authenticated) for a short-lived signed URL, then send the browser to
 * it. The server signs `Content-Disposition: attachment`, so the browser saves
 * rather than renders.
 *
 * Errors are worth handling at the call site: this is the one path that can
 * come back 403 `lesson_not_available` (the resource is dripped) or
 * `purchase_required` (a preview lesson's worksheet is still paid content).
 */
export async function downloadLessonFile(courseId: string, lessonId: string, fileId: string) {
  const res = await api.get<{ url: string; filename: string; expiresInSeconds: number }>(
    `/courses/${courseId}/lessons/${lessonId}/files/${fileId}/download`,
  );
  // Same wall as the resource library: in the app this navigation never
  // produced a file. The server already tells us the filename, so the
  // notification and the saved file are named properly.
  if (canDownloadUrlNative()) {
    if (await downloadUrlNative({ url: res.data.url, filename: res.data.filename })) return;
  }
  window.open(res.data.url, '_self', 'noopener');
}

/**
 * Embed URL for an Office document (xlsx / docx / pptx), for the in-app viewer.
 *
 * Composed server-side (so the SPA can't be pointed at another renderer) and
 * gated on `member_resources_office_viewer` — 404 when the flag is off, 409
 * `office_viewer_unavailable` when the deployment's storage endpoint isn't
 * reachable by the external renderer (local dev).
 */
export async function resourceOfficeEmbed(
  id: string,
): Promise<{ embedUrl: string; expiresInSeconds: number }> {
  const res = await api.get<{ embedUrl: string; expiresInSeconds: number }>(
    `/resources/${id}/office-view`,
  );
  return res.data;
}

// ── GC Redeem catalog (member-facing) ──────────────────────────────────────
/**
 * The redeem catalog: redeem-only products priced in GC (flat `priceGc`, no PV,
 * no discount). Disjoint from `getStorefront()`. `context.balances.gc` gates
 * whether the member can afford a redemption.
 */
export async function getRedeemCatalog(slotId?: string | null): Promise<StorefrontResponse> {
  const res = await api.get<StorefrontResponse>('/storefront/redeem', {
    params: slotId ? { slotId } : {},
  });
  return res.data;
}

// ── Replicated landing page (public) ───────────────────────────────────────
export type ReplicatedBlock = {
  id: string;
  type: 'heading' | 'text' | 'image' | 'video' | 'button';
  align: 'left' | 'center' | 'right';
  content: Record<string, unknown>;
  sortOrder: number;
};

export type ReplicatedPageResponse = {
  enabled: boolean;
  /**
   * LEGACY shape, still returned for one release so a member bundle older than
   * the API keeps working. The renderer reads `layout`.
   */
  blocks: ReplicatedBlock[];
  /**
   * The page's content. `version: 0` is a page converted on read from
   * pre-builder blocks (rendered in the historic narrow column); `version: 1` is
   * builder-authored, with full-width bands.
   */
  layout?: RpLayout | null;
  /** Page identity — keys per-visitor countdown anchors by page, not URL. */
  pageId?: string | null;
  /** Kit cards for a `packages` section, resolved from the page's tagging. */
  packages?: {
    productId: string;
    name: string;
    price?: string;
    pv?: string;
    imageUrl?: string;
  }[];
  /** Editable signup-CTA wording; null when the page is disabled. */
  ctaLabel?: string | null;
  /** Whether to append the automatic signup button below the content. */
  ctaAuto?: boolean;
  /**
   * The product the automatic CTA sells (`/buy/<id>` — register if needed, then
   * checkout). Null/absent = it goes to the registration form. Optional so an
   * API older than this bundle degrades to sign-up rather than breaking.
   */
  ctaProductId?: string | null;
  /** Rolling per-visitor countdown banner config; null/absent = no banner. */
  timer?: { hours: number; label: string; pageId: string } | null;
};

// A named campaign page's blocks (or the default page when `slug` is omitted).
export async function getReplicatedPage(slug?: string): Promise<ReplicatedPageResponse> {
  const url = slug ? `/replicated-page/${encodeURIComponent(slug)}` : '/replicated-page';
  const res = await api.get<ReplicatedPageResponse>(url);
  return res.data;
}

// The tenant's enabled campaign pages, for building per-campaign share links.
export type ReplicatedPageSummary = {
  slug: string;
  title: string;
  isDefault: boolean;
  /** Activation kits this campaign narrows to ([] = all the member's packages). */
  kitProductIds: string[];
};
export async function listReplicatedPages(): Promise<{
  enabled: boolean;
  pages: ReplicatedPageSummary[];
}> {
  const res = await api.get<{ enabled: boolean; pages: ReplicatedPageSummary[] }>(
    '/replicated-pages',
  );
  return res.data;
}

export function replicatedBlockImageUrl(blockId: string): string {
  return `${BASE_URL}/replicated-page/blocks/${blockId}/image`;
}

/**
 * The API origin, for callers that resolve stored-relative URLs inside content
 * (the replicated page absolutizing inline rich-text image srcs).
 */
export const API_BASE_URL = BASE_URL;

// ── Member subscription (Phase C) ────────────────────────────────────
export type SubscriptionInfo = {
  enabled: boolean;
  plan: { price: string; currency: 'cash' | 'gc' };
  subscription: {
    status: string;
    provider: string;
    currentPeriodEnd: string | null;
    slotId: string | null;
    currency: string;
    isActive: boolean;
  } | null;
};

export async function getMySubscription(): Promise<SubscriptionInfo> {
  const res = await api.get<SubscriptionInfo>('/me/subscription');
  return res.data;
}

export async function startWalletSubscription(
  slotId: string,
): Promise<{ status: string; currentPeriodEnd: string | null; isActive: boolean }> {
  const res = await api.post('/me/subscription/wallet/start', { slotId });
  return res.data;
}

export async function cancelMySubscription(): Promise<void> {
  await api.post('/me/subscription/cancel');
}

// Special links — subscription-gated. The list carries metadata ONLY (no URL);
// the URL is resolved per-open behind the subscription gate.
export type SpecialLinkMeta = { id: string; title: string; type: 'zoom' | 'vimeo' | 'other' };

export async function getSpecialLinks(): Promise<SpecialLinkMeta[]> {
  const res = await api.get<SpecialLinkMeta[]>('/me/special-links');
  return res.data;
}

export type SpecialLinkOpen =
  | { kind: 'vimeo_embed'; embedUrl: string; title: string }
  | { kind: 'redirect'; url: string; title: string };

export async function openSpecialLink(id: string): Promise<SpecialLinkOpen> {
  const res = await api.post<SpecialLinkOpen>(`/me/special-links/${id}/open`);
  return res.data;
}

// ── LMS: courses ───────────────────────────────────────────────────────────

export type CourseCategory = {
  id: string;
  name: string;
  sortOrder: number;
  /** Exactly ONE level of nesting; a root category's parent is null. */
  parentId: string | null;
  /** Storefront-visible courses filed under it; a ROOT counts its children too. */
  courseCount: number;
  /** A picture has been uploaded — render `courseCategoryImageUrl(id)`. */
  hasImage: boolean;
  /** Cover fallback when no picture was uploaded: the topic's first course. */
  coverProductId: string | null;
};
export async function listCourseCategories() {
  const res = await api.get<CourseCategory[]>('/storefront/course-categories');
  return res.data;
}

/**
 * A course topic's picture. Public and addressed by id, like every other
 * `<img>`-consumed asset route — a browser image request carries no
 * Authorization header, so a guarded one would simply never render.
 */
export function courseCategoryImageUrl(id: string) {
  return `${BASE_URL}/storefront/course-categories/${id}/image`;
}

/** Tenant-wide learning figures — the `/courses` hero's counts + rating card. */
export type LearningStats = {
  courseCount: number;
  mentorCount: number;
  learnerCount: number;
  rating: { average: number; count: number };
};
export async function getLearningStats() {
  const res = await api.get<LearningStats>('/storefront/learning/stats');
  return res.data;
}

/**
 * One mentor on the `/courses` instructors row. HOUSE accounts never appear —
 * that account IS the company.
 */
export type PublicInstructor = {
  userId: string;
  displayName: string;
  hasAvatar: boolean;
  /** The topic they publish most in; null when none of their courses is filed. */
  specialty: string | null;
  courseCount: number;
  studentCount: number;
  ratingAvg: number;
  ratingCount: number;
};
export async function listInstructors(limit = 12) {
  const res = await api.get<{ instructors: PublicInstructor[] }>('/storefront/instructors', {
    params: { limit },
  });
  return res.data.instructors;
}

export type CourseTag = { id: string; name: string; sortOrder: number; active: boolean };
export async function listCourseTags() {
  const res = await api.get<CourseTag[]>('/storefront/course-tags');
  return res.data;
}

/**
 * The paged catalog call. Every filter is applied SERVER-side, so the SPA never
 * pages a filtered subset of one over-fetched page — which is what the old
 * client-side filter did, silently hiding matches past the feed's own limit.
 */
export async function listCourseCatalog(
  opts: CourseCatalogQuery = {},
): Promise<{ courses: CourseSummary[]; total: number }> {
  const res = await api.get<{ courses: CourseSummary[]; total: number }>('/storefront/courses', {
    params: {
      ...(opts.productIds?.length ? { productIds: opts.productIds.join(',') } : {}),
      ...(opts.creator ? { creator: opts.creator } : {}),
      ...(opts.search ? { search: opts.search } : {}),
      ...(opts.categoryId ? { categoryId: opts.categoryId } : {}),
      ...(opts.tagId ? { tagId: opts.tagId } : {}),
      ...(opts.level ? { level: opts.level } : {}),
      ...(opts.accessLevel ? { accessLevel: opts.accessLevel } : {}),
      ...(opts.language ? { language: opts.language } : {}),
      ...(opts.sort ? { sort: opts.sort } : {}),
      ...(opts.excludeOwned ? { excludeOwned: opts.excludeOwned } : {}),
      ...(opts.limit != null ? { limit: opts.limit } : {}),
      ...(opts.offset != null ? { offset: opts.offset } : {}),
    },
  });
  return res.data;
}

/** Array-shaped wrapper kept for the callers that only ever wanted the rows. */
export async function listCourseSummaries(opts: CourseCatalogQuery = {}) {
  return (await listCourseCatalog(opts)).courses;
}

export async function listMyCreatorSubscriptions() {
  const res = await api.get<MyCreatorSubscription[]>('/me/subscriptions/creators');
  return res.data;
}

/** A one-time Stripe Customer Portal link (update card, cancel or resume, invoices). */
export async function openBillingPortal() {
  const res = await api.post<{ url: string }>('/me/subscriptions/billing-portal', {});
  return res.data;
}

/** Turn auto-renew for one creator on (re-consents at today's price) or off. */
export async function setCreatorAutoRenew(creatorUserId: string, enabled: boolean) {
  const res = await api.patch<CreatorAutoRenewState>(
    `/me/subscriptions/creators/${creatorUserId}/auto-renew`,
    { enabled },
  );
  return res.data;
}

export async function getPublicCourse(productId: string) {
  const res = await api.get<PublicCourse>(`/storefront/courses/${productId}`);
  return res.data;
}

// ── LMS: course reviews (enrollment-based, not order-line based) ────────────
export type CourseReview = {
  id: string;
  rating: number;
  comment: string | null;
  /** Phase 8: a moderator took this review down. The AUTHOR still gets it back
   *  (flagged) — silently hiding it would read as "my review vanished". */
  hidden: boolean;
  createdAt: string;
  updatedAt: string;
};

/** Why this learner may not review yet — the SPA turns it into helper copy. */
export type CourseReviewIneligibleReason = 'not_enrolled' | 'no_progress';

export type MyCourseReview = {
  review: CourseReview | null;
  eligible: boolean;
  reason?: CourseReviewIneligibleReason;
};

export async function getMyCourseReview(courseId: string) {
  const res = await api.get<MyCourseReview>(`/courses/${courseId}/review/mine`);
  return res.data;
}

/**
 * Create OR edit — the server upserts on both verbs, so the SPA picks the verb
 * purely for HTTP honesty and never has to reconcile a 409.
 */
export async function submitCourseReview(
  courseId: string,
  input: { rating: number; comment?: string | null },
  opts: { edit?: boolean } = {},
) {
  const res = await (opts.edit ? api.patch : api.post)<{ review: CourseReview }>(
    `/courses/${courseId}/review`,
    input,
  );
  return res.data.review;
}

// ── Wishlist ("Saved") — products, so one API serves shop + course tiles ────
export type WishlistItem = { productId: string; createdAt: string };

export async function getWishlist() {
  const res = await api.get<{ items: WishlistItem[] }>('/me/wishlist');
  return res.data.items;
}

export async function addToWishlist(productId: string) {
  const res = await api.put<{ productId: string; wished: boolean }>(`/me/wishlist/${productId}`);
  return res.data;
}

export async function removeFromWishlist(productId: string) {
  const res = await api.delete<{ productId: string; wished: boolean }>(`/me/wishlist/${productId}`);
  return res.data;
}

export type CourseAnnouncementItem = {
  id: string;
  courseId: string;
  title: string;
  body: string;
  publishedAt: string | null;
  createdAt: string;
};

/** Published announcements for a course the caller can access. */
export async function listCourseAnnouncements(courseId: string) {
  const res = await api.get<CourseAnnouncementItem[]>(`/courses/${courseId}/announcements`);
  return res.data;
}

export async function getCoursePlayer(courseId: string) {
  const res = await api.get<CoursePlayer>(`/courses/${courseId}/player`);
  return res.data;
}
export async function enrollCourse(courseId: string) {
  await api.post(`/courses/${courseId}/enroll`);
}
/** OPTIONAL auth server-side: a `isPreview` lesson resolves for a logged-out
 *  visitor too (everything else 401s), so the storefront course page can play a
 *  free preview in place. */
export async function openLesson(
  courseId: string,
  lessonId: string,
  opts: { refresh?: boolean } = {},
) {
  // `refresh` re-presigns an expiring R2 URL WITHOUT counting as a play: it
  // skips the progress upsert, the daily view bump and the drip re-check, so a
  // mid-lesson TTL swap cannot inflate "views = plays" or 403 a lesson the
  // learner passed the gate for minutes ago.
  const res = await api.post<LessonSource>(
    `/courses/${courseId}/lessons/${lessonId}/open`,
    opts.refresh ? { refresh: true } : {},
  );
  return res.data;
}

/**
 * Caption tracks are streamed through the API, not fetched from storage: the
 * `src` on a LessonSource is an API path. Pulling the VTT as text here (with
 * the normal Authorization header) lets the player hand `<track>` a same-origin
 * `blob:` URL, which is what keeps the `<video>` free of a `crossorigin`
 * attribute — and therefore keeps playback independent of the R2 bucket's
 * hand-maintained CORS policy.
 */
export async function fetchCaptionVtt(src: string) {
  const res = await api.get<string>(src, {
    responseType: 'text',
    transformResponse: [(data: string) => data],
  });
  return res.data;
}
export async function completeLesson(courseId: string, lessonId: string, via: 'player' | 'manual') {
  const res = await api.post<{
    newlyCompleted: boolean;
    courseComplete: boolean;
    certificateIssued: boolean;
    hasCertificate: boolean;
    testsPassed: boolean;
  }>(`/courses/${courseId}/lessons/${lessonId}/complete`, { via });
  return res.data;
}

// ── course tests (the quiz gate before the certificate) ───────────────────
export type MemberTestQuestion = {
  id: string;
  kind: 'multiple_choice' | 'essay';
  prompt: string;
  choices: string[];
  points: number;
};
export type MemberTest = {
  id: string;
  chapterId: string | null;
  title: string;
  description: string | null;
  passingScorePct: number;
  maxAttempts: number | null;
  isPractice: boolean;
  questions: MemberTestQuestion[];
};
export async function getCourseTest(courseId: string, testId: string) {
  const res = await api.get<MemberTest>(`/courses/${courseId}/tests/${testId}`);
  return res.data;
}
export type MemberAttemptEntry = {
  questionId: string;
  kind: 'multiple_choice' | 'essay';
  prompt: string;
  choices: string[];
  points: number;
  selectedIndex: number | null;
  text: string | null;
  /** null while the attempt is pending review. */
  awardedPoints: number | null;
  /** MC only; null while pending or for essays. */
  correct: boolean | null;
  comment: string | null;
  /**
   * The answer key, present ONLY on a practice attempt (a graded payload omits
   * both keys entirely). Named so it cannot collide with the server-only
   * `correctIndex` the leak test forbids in any member payload.
   */
  solutionIndex?: number | null;
  explanation?: string | null;
};
export type MemberAttempt = {
  id: string;
  testId: string;
  attemptNumber: number;
  status: 'pending_review' | 'passed' | 'failed';
  scorePct: number | null;
  maxScore: number;
  submittedAt: string;
  gradedAt: string | null;
  entries: MemberAttemptEntry[];
};
export async function submitTestAttempt(
  courseId: string,
  testId: string,
  answers: { questionId: string; selectedIndex?: number | null; text?: string | null }[],
) {
  const res = await api.post<{
    attempt: MemberAttempt;
    certificateIssued: boolean;
    hasCertificate: boolean;
  }>(`/courses/${courseId}/tests/${testId}/attempts`, { answers });
  return res.data;
}
export async function getTestAttempt(courseId: string, testId: string, attemptId: string) {
  const res = await api.get<MemberAttempt>(
    `/courses/${courseId}/tests/${testId}/attempts/${attemptId}`,
  );
  return res.data;
}
export async function uncompleteLesson(courseId: string, lessonId: string) {
  await api.delete(`/courses/${courseId}/lessons/${lessonId}/complete`);
}

export async function listMyCourses(cursor?: string | null) {
  const res = await api.get<{ items: MyCourse[]; nextCursor: string | null }>('/me/courses', {
    params: cursor ? { cursor } : {},
  });
  return res.data;
}

export type ContinueLearning = {
  courseId: string;
  productId: string;
  courseTitle: string;
  lessonId: string;
  lessonTitle: string;
  lessonIndex: number;
  lessonCount: number;
  completedCount: number;
  lastOpenedAt: string;
  lessonsDone: boolean[];
} | null;
export async function getLearningDashboard() {
  const res = await api.get<{ continueLearning: ContinueLearning; catalogPicks: CourseSummary[] }>(
    '/me/learning/dashboard',
  );
  return res.data;
}

export type CreatorProfile = {
  userId: string;
  displayName: string;
  bio: string | null;
  hasAvatar: boolean;
  links: { label: string; url: string }[];
  since: string | null;
  /** The tenant's own staff account — the card says "House", not a pitch. */
  isHouse: boolean;
  /** Storefront-visible courses only; both gated by `showCourseRatings` in UI. */
  courseCount: number;
  studentCount: number;
  courses: CourseSummary[];
};
export async function getCreatorProfile(id: string) {
  const res = await api.get<CreatorProfile>(`/creators/${id}/profile`);
  return res.data;
}
export function creatorAvatarUrl(id: string) {
  return `${BASE_URL}/creators/${id}/avatar`;
}

// ── Instructor panel: live stats, reviews, follow ──
// The course-side twin of the merchant shop stat row. Stats and reviews are
// PUBLIC (a guest browsing the catalog sees real numbers); follow needs auth.

export type CreatorStats = {
  creatorUserId: string;
  displayName: string;
  isHouse: boolean;
  joinedAt: string;
  courseCount: number;
  videoCount: number;
  r2VideoCount: number;
  studentCount: number;
  /** Null under the tenant-wide subscription model — not applicable, not zero. */
  subscriberCount: number | null;
  followers: number;
  rating: { average: number; count: number };
  transactions: number;
};
export async function getCreatorStats(id: string): Promise<CreatorStats> {
  const res = await api.get<CreatorStats>(`/creators/${id}/stats`);
  return res.data;
}

export type CreatorReview = {
  id: string;
  courseId: string;
  courseTitle: string;
  rating: number;
  comment: string | null;
  reviewer: string;
  createdAt: string;
};
export async function listCreatorReviews(
  id: string,
  opts: { limit?: number; offset?: number } = {},
): Promise<{ rows: CreatorReview[]; total: number }> {
  const res = await api.get<{ rows: CreatorReview[]; total: number }>(`/creators/${id}/reviews`, {
    params: opts,
  });
  return res.data;
}

export async function getCreatorFollowState(id: string): Promise<{ following: boolean }> {
  const res = await api.get<{ following: boolean }>(`/creators/${id}/follow-state`);
  return res.data;
}
export async function followCreator(
  id: string,
): Promise<{ following: boolean; followers: number }> {
  const res = await api.post<{ following: boolean; followers: number }>(`/creators/${id}/follow`);
  return res.data;
}
export async function unfollowCreator(
  id: string,
): Promise<{ following: boolean; followers: number }> {
  const res = await api.delete<{ following: boolean; followers: number }>(`/creators/${id}/follow`);
  return res.data;
}
export async function createCreatorOrder(input: {
  creatorUserId: string;
  items: { productId: string; qty: number; autoRenew?: boolean }[];
  /** Manual or cash-wallet method chosen at checkout; omitted = first manual. */
  paymentMethodCode?: string;
  /** Which slot's wallet pays (per-slot balances); omitted = earliest active. */
  buyerSlotId?: string;
  checkoutGroupId?: string;
  notes?: string;
  /** A CREATOR-owned coupon code. House/merchant codes are refused server-side. */
  couponCode?: string;
}) {
  const res = await api.post<{
    id: string;
    code: string;
    total: string;
    totalPv?: string;
    status?: string;
    checkoutGroupId: string | null;
  }>('/creator-orders', input);
  return res.data;
}

// ── LMS: certificates + Q&A ────────────────────────────────────────────────
export type CertificatePlaceholder = {
  field: 'member_name' | 'course_title' | 'issued_date' | 'verify_code';
  xPct: number;
  yPct: number;
  widthPct: number;
  fontSizePx: number;
  color: string;
  align: 'left' | 'center' | 'right';
};
export type CertificateRender = {
  id: string;
  memberName: string;
  courseTitle: string;
  verificationCode: string;
  issuedAt: string;
  /** Presigned R2 URL — the FALLBACK; needs bucket CORS to draw on a canvas. */
  imageUrl: string;
  /** API path for the same image; preferred, and needs no bucket CORS. */
  imagePath: string;
  config: { placeholders: CertificatePlaceholder[] };
};

export type MyCertificate = {
  id: string;
  courseId: string;
  courseTitle: string;
  memberName: string;
  verificationCode: string;
  issuedAt: string;
};

/** The certificate background, addressed by its public verification code. */
export function certificateImageUrl(code: string) {
  return `${BASE_URL}/certificates/${encodeURIComponent(code)}/image`;
}

/** One of the member's gift checks (`GET /me/vouchers`). */
export type MyVoucher = {
  id: string;
  code: string;
  status: 'issued' | 'redeemed' | 'expired' | 'revoked';
  programId: string;
  programName: string;
  description: string | null;
  discountType: 'percent' | 'amount';
  discountValue: string;
  discountScope: 'one_unit' | 'every_unit';
  appliesToAll: boolean;
  productIds: string[];
  productNames: string[];
  issuedReason: string;
  issuedAt: string;
  expiresAt: string | null;
  redeemedAt: string | null;
};

/** How far the member is toward the next gift check of one program. */
export type MyVoucherProgress = {
  programId: string;
  programName: string;
  description: string | null;
  discountType: 'percent' | 'amount';
  discountValue: string;
  discountScope: 'one_unit' | 'every_unit';
  earnCount: number;
  count: number;
  /** Names of the products whose units count. Empty with `allProducts`. */
  earnProductNames: string[];
  allProducts: boolean;
};

export type MyVouchersPage = {
  usable: MyVoucher[];
  progress: MyVoucherProgress[];
  history: MyVoucher[];
};

export async function getMyVouchers() {
  const res = await api.get<MyVouchersPage>('/me/vouchers');
  return res.data;
}

/** Where the member stands on their birthday gift checks (cashier phase 5). */
export type MyBirthdayVouchers = {
  programs: Array<{
    id: string;
    name: string;
    description: string | null;
    discountType: 'percent' | 'amount';
    discountValue: string;
    discountScope: 'one_unit' | 'every_unit';
    validDays: number | null;
  }>;
  birthDate: string | null;
  birthDateEditable: boolean;
  kycRequired: boolean;
  window: { open: boolean; year: number; opensOn: string; closesOn: string } | null;
  claimedProgramIds: string[];
  canClaim: boolean;
};

export async function getMyBirthdayVouchers() {
  const res = await api.get<MyBirthdayVouchers>('/me/vouchers/birthday');
  return res.data;
}

export async function claimBirthdayVouchers() {
  const res = await api.post<{ issued: Array<{ id: string; code: string; programName: string }> }>(
    '/me/vouchers/claim-birthday',
  );
  return res.data;
}

export async function listMyCertificates() {
  const res = await api.get<MyCertificate[]>('/me/certificates');
  return res.data;
}
export async function getMyCertificate(courseId: string) {
  const res = await api.get<CertificateRender>(`/courses/${courseId}/certificate`);
  return res.data;
}
/**
 * "No such certificate" is an ANSWER, not a failure: the route says so with a
 * 404 and a `{valid:false}` body. Accepting that status is what lets the verify
 * page tell a genuine network problem apart from a code that does not exist —
 * the page used to render both as "no match", which tells someone holding a
 * real certificate that it is fake.
 */
export async function verifyCertificate(code: string) {
  const res = await api.get<{
    valid: boolean;
    id?: string;
    memberName?: string;
    courseTitle?: string;
    issuedAt?: string;
    creatorName?: string | null;
    /** Both present on a valid row, so the page can DRAW the certificate. */
    verificationCode?: string;
    templateConfig?: { placeholders: CertificatePlaceholder[] };
  }>(`/certificates/verify/${encodeURIComponent(code)}`, {
    validateStatus: (s) => s === 200 || s === 404,
  });
  return res.data;
}

export type LessonQuestion = {
  id: string;
  lessonId: string;
  parentId: string | null;
  body: string;
  isCreatorReply: boolean;
  hidden: boolean;
  createdAt: string;
  authorName: string | null;
  isMine: boolean;
};
export async function listLessonQuestions(courseId: string, lessonId: string) {
  const res = await api.get<LessonQuestion[]>(`/courses/${courseId}/lessons/${lessonId}/questions`);
  return res.data;
}
export async function postLessonQuestion(courseId: string, lessonId: string, body: string) {
  const res = await api.post<LessonQuestion>(`/courses/${courseId}/lessons/${lessonId}/questions`, {
    body,
  });
  return res.data;
}
export async function replyToQuestion(questionId: string, body: string) {
  const res = await api.post<LessonQuestion>(`/questions/${questionId}/reply`, { body });
  return res.data;
}

// ── learner notes + bookmarks ──────────────────────────────────────────────
/** A note with an empty body IS a bookmark — same row, no second table. */
export type LessonNote = {
  id: string;
  lessonId: string;
  courseId: string;
  positionSeconds: number | null;
  body: string;
  createdAt: string;
  updatedAt: string;
};
export async function listCourseNotes(
  courseId: string,
  opts: { lessonId?: string; limit?: number } = {},
) {
  const res = await api.get<{ notes: LessonNote[] }>(`/courses/${courseId}/notes`, {
    params: {
      ...(opts.lessonId ? { lessonId: opts.lessonId } : {}),
      ...(opts.limit ? { limit: opts.limit } : {}),
    },
  });
  return res.data.notes;
}
export async function createLessonNote(
  courseId: string,
  lessonId: string,
  input: { positionSeconds?: number | null; body?: string },
) {
  const res = await api.post<LessonNote>(`/courses/${courseId}/lessons/${lessonId}/notes`, input);
  return res.data;
}
export async function updateLessonNote(courseId: string, noteId: string, body: string) {
  const res = await api.patch<LessonNote>(`/courses/${courseId}/notes/${noteId}`, { body });
  return res.data;
}
export async function deleteLessonNote(courseId: string, noteId: string) {
  await api.delete(`/courses/${courseId}/notes/${noteId}`);
}

// ── Phase 8: member abuse reporting ────────────────────────────────────────
export type ContentReportResult = {
  /** Already had an OPEN report on this target — the server's word, not an error. */
  alreadyReported: boolean;
  id?: string;
};

/**
 * File a report against a course, review, question or announcement.
 *
 * The 200/201 split is deliberate on the server: a repeat report from the same
 * member is answered with `alreadyReported: true` rather than a 409, so the
 * caller must branch on the FIELD, not on the status code.
 */
export async function reportContent(input: {
  targetKind: ContentReportTarget;
  targetId: string;
  reason: ContentReportReason;
  details?: string;
}): Promise<ContentReportResult> {
  const res = await api.post<{ ok: true; id?: string; alreadyReported?: boolean }>(
    '/reports',
    input,
  );
  return { alreadyReported: res.data.alreadyReported === true, id: res.data.id };
}
